/**
 * /manage/apps/{id} — one web app: what it is, what it's doing, and the
 * levers that change that.
 *
 * Eight tabs, in the order an operator reaches for them:
 *   Overview   — status, addresses, webhooks, routes, version history (with
 *                roll back), and the provision log + events.
 *   Deliveries — the inbound webhook log: what arrived, whether it was
 *                signed, what the app did with it, and a replay.
 *   Schedules  — the declared crons, "run now", and the run history.
 *   Credentials— what the app can reach, what it authenticates with, and
 *                whether that still works (status, last error, failures in
 *                24h, token expiry, a live Test and the rotation history).
 *                Write-only: there is no reveal here or anywhere else, and
 *                a connection the AGENT asked for (the in-chat card, §7.2)
 *                shows as pending until the person in the chat fills it.
 *   API keys   — the reverse direction (F8): what other systems call THIS
 *                app with. The one place a value is ever shown — once, in
 *                the dialog that mints it; afterwards only the prefix.
 *   Logs       — the app's own log tail, with an auto-refresh toggle.
 *   Storage    — live cpu/memory/pids for a warm app and what it has stored.
 *   Danger     — pause/resume, restart, archive. Each one is destructive to a
 *                live URL, which is why they live here and not on the agent's
 *                MCP surface.
 *
 * Deliveries and Schedules follow the same rule as the danger zone: replaying
 * a delivery re-runs a handler with its real side effects, running a cron does
 * the same, and rotating a signing secret stops whatever is signing with the
 * old one. Operator verbs, not agent ones.
 *
 * `id` is webgen's app id (the slug also resolves, but ids survive a rename).
 * Everything proxies through /admin/webgen/apps — webgen is the
 * system-of-record, CD stores nothing about apps.
 *
 * Nothing on this page wakes a sleeping app: reads come from webgen's own
 * rows. (Waking on purpose is what `GET …/smoke` is for — the "is this thing
 * on" check, deliberately not wired to a page load.)
 */

import { ApiError } from "@chai/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import {
  Archive,
  ArrowLeft,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  KeyRound,
  LogIn,
  Pause,
  Play,
  Plus,
  Redo2,
  RefreshCw,
  RotateCcw,
  RotateCw,
  Trash2,
  Zap,
} from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";

import { useApi } from "@/api";
import { PageHeader } from "@/components/manage/PageHeader";
import { ArtifactPeoplePanel } from "@/components/manage/ArtifactPeoplePanel";
import { Section } from "@/components/manage/Section";
import { StatStrip } from "@/components/manage/StatStrip";
import { StatusBadge } from "@/components/manage/StatusBadge";
import { useIsDemoManage } from "@/components/manage/org-context";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  ACCESS_TONE,
  APPS_KEY,
  APP_STATUS_TONE,
  type AppWebhook,
  type WebgenAppRow,
} from "./manage.apps.index";

export const Route = createFileRoute("/manage/apps/$id")({
  component: AppDetailPage,
});

interface AppEvent {
  kind: string;
  at: string;
  actor?: string;
  msg?: string;
  level?: string;
  data?: Record<string, unknown>;
}

interface AppVersion {
  version: number;
  health: string | null;
  notes: string | null;
  author: string | null;
  routes: string[];
  bytes?: { code?: number; static?: number };
  files?: { path: string; size: number }[];
  live?: boolean;
  created_at: string;
}

interface StorageUsage {
  documents?: number;
  kv?: number;
  blob_bytes?: number;
}

/** The `cd:` block from the app's manifest — what this app may read from the
 *  workspace (F1, `docs/data-loop-plan.md` §2.2). Values are a list of table
 *  patterns, `true`, or `"*"`; the shape is deliberately open because the
 *  vocabulary grows a key per phase (F3 adds the create-only write verbs) and
 *  this card should show a key it has never heard of rather than hide it. */
type Capabilities = Record<string, unknown>;

interface AppDetail extends WebgenAppRow {
  storage_usage: StorageUsage;
  recent_events: AppEvent[];
  versions: AppVersion[];
  /** The manifest's `cd:` block VERBATIM (webgen's `declared_capabilities`),
   *  or null when the app declares none. Absent entirely on a webgen that
   *  predates the data plane — which is why the card renders nothing rather
   *  than "none": "this deployment cannot tell you" and "this app declared
   *  nothing" are different facts. */
  capabilities?: Capabilities | null;
}

/** One org-admin's standing approval of an ACTION capability (F4).
 *  `runs.start` and `mail.send` need one on top of the manifest declaration:
 *  the app declares, a person approves, and the row IS the approval —
 *  revoking is deleting it. */
interface Approval {
  app_id: string;
  capability: string;
  approved_by: string | null;
  approved_by_email: string | null;
  note: string | null;
  created_at: string | null;
}

/** One workflow run this app started. Keyed on `started_by_ref`, so a run a
 *  PERSON started on the same canvas never appears here. */
interface AppWorkflowRun {
  run_id: string;
  workflow_id: string;
  phase: string;
  canvas_id: string;
  thread_id: string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string | null;
  error_message: string | null;
}

interface ProvisionStep {
  at: string;
  ok: boolean;
  step: string;
  detail: string;
  took_ms: number;
}

interface AppStats {
  app: string;
  warm: boolean;
  status: string;
  cpu_percent?: number | null;
  memory_bytes?: number | null;
  memory_limit_bytes?: number | null;
  pids?: number | null;
  storage_usage: StorageUsage;
  provision_log: ProvisionStep[];
}

interface AppLogs {
  app: string;
  version: number;
  lines: string[];
  truncated: boolean;
}

/** One inbound webhook delivery. `verdict` (was it signed) and `status` (what
 *  the app did with it) are deliberately separate columns: a rejected
 *  signature and a handler that threw are different faults with different
 *  fixes, and collapsing them into one chip would hide which happened. */
interface Delivery {
  id: string;
  endpoint: string;
  received_at: string;
  /** verified · rejected · unsigned */
  verdict: string;
  /** received · delivered · failed */
  status: string;
  attempts: number;
  response_status: number | null;
  last_error: string | null;
  bytes: number;
  /** Set on the clone a replay creates — the id of the delivery it repeats. */
  replay_of?: string | null;
}

/** One file POSTed to the app's ingest door (`GET …/ingests`). */
interface Ingest {
  id: string;
  name: string;
  bytes: number;
  content_type: string;
  received_at: string;
  /** received · delivered · failed — what happened when the handler ran */
  status: string;
  attempts: number;
  response_status: number | null;
  last_error: string | null;
  /** ok · failed · skipped — whether the data room took the file */
  forward: string | null;
  forwarded_asset_id: string | null;
  forward_error: string | null;
}

interface DeliveryDetail extends Delivery {
  /** The headers webgen captured. */
  headers?: Record<string, string>;
  /** Clipped at 64 KB (`body_clipped`) and base64-encoded when the payload is
   *  not UTF-8 (`body_base64`) — binary arrives readable, not mangled. */
  body_text?: string | null;
  body_base64?: boolean;
  body_clipped?: boolean;
}

/** One scheduled run. `skipped` is a normal outcome — the previous run was
 *  still going — not a failure. */
interface AppRun {
  id: string;
  name: string;
  scheduled_at: string;
  started_at: string | null;
  finished_at: string | null;
  /** scheduled · running · ok · error · skipped */
  status: string;
  duration_ms: number | null;
  error: string | null;
}

/** One credential the app can use, as webgen reports it — the non-secret
 *  half and nothing else. There is no read-back on any surface: `config` is
 *  webgen's filtered display form (a header name, a username), never a value.
 *  `status` separates a connection that is configured from one the AGENT
 *  asked for through the in-chat card and nobody has filled yet. */
interface Connection {
  name: string;
  kind: string;
  base_url: string;
  /** configured · pending · failing (older webgens also send `error`) */
  status: string;
  config?: Record<string, unknown>;
  updated_at?: string | null;
  /** ── health (F8) ── The list above says what a connection IS; these say
   *  whether it still works. A credential that quietly expired looks exactly
   *  like one that never did until something calls it, which is the failure
   *  this half exists to make visible. All optional: a webgen that predates
   *  them renders the row exactly as it did before. */
  last_used_at?: string | null;
  last_error?: string | null;
  failures_24h?: number | null;
  /** OAuth2 kinds only: when the access token webgen is holding runs out. */
  token_expires_at?: string | null;
  secret_version?: number | null;
  rotated_at?: string | null;
}

/** One entry in a connection's rotation history. Hints and actors — there is
 *  no read-back of any version, past or present. */
interface ConnectionVersion {
  version: number;
  hint: string | null;
  rotated_at: string | null;
  actor: string | null;
}

/** The result of the Test button: one real call through the egress proxy. */
interface ConnectionTest {
  ok: boolean;
  status?: number | null;
  took_ms?: number | null;
  error?: string | null;
}

/** One API key somebody else calls this app WITH (F8).
 *
 *  The reverse of a connection: a connection is what the app authenticates
 *  outward with, a key is what a partner authenticates inward with. `prefix`
 *  is all that is kept of the value — webgen stores a hash — so a revoked row
 *  stays in the list: "there used to be a key called partner-read and it was
 *  revoked on the 3rd" is exactly what somebody chasing a 401 needs. */
interface AppKey {
  id: string;
  name: string;
  prefix: string;
  scopes: KeyScope[];
  rate_rpm?: number | null;
  created_at: string;
  last_used_at?: string | null;
  calls_total?: number | null;
  revoked_at?: string | null;
}

/** One grant on a key: these methods, under this path. */
interface KeyScope {
  methods?: string[];
  path?: string;
}

const LOG_TAIL = 200;
const DELIVERY_LIMIT = 50;
const RUN_LIMIT = 50;

type Tone = "ready" | "pending" | "failed" | "neutral" | "info";

/** Signature verdict. `unsigned` is amber, not green: it means the endpoint
 *  accepted something nobody proved they sent. */
const VERDICT_TONE: Record<string, Tone> = {
  verified: "ready",
  rejected: "failed",
  unsigned: "pending",
};

const DELIVERY_STATUS_TONE: Record<string, Tone> = {
  delivered: "ready",
  received: "info",
  failed: "failed",
};

const RUN_STATUS_TONE: Record<string, Tone> = {
  ok: "ready",
  running: "pending",
  scheduled: "neutral",
  skipped: "neutral",
  error: "failed",
};

/** `pending` is amber, not grey: the agent asked for this credential and the
 *  app cannot reach anything until someone answers the card in the chat. */
const CONNECTION_STATUS_TONE: Record<string, Tone> = {
  configured: "ready",
  ready: "ready",
  pending: "pending",
  awaiting: "pending",
  // W5: the client is set and nobody has signed in at the vendor yet — the fix
  // is the Authorize button on the row.
  unauthorized: "pending",
  // `failing` is webgen's word for "the credential is there and the calls are
  // being refused" — a different fault from "nobody filled it in", and the
  // only one where the fix is a NEW card rather than a first one.
  failing: "failed",
  error: "failed",
};

/** The auth shapes webgen accepts, and what each one needs typed.
 *
 *  `secretShape` is what goes into the PUT body's `secret`; `configField` is
 *  the NON-secret half that is safe to show back on the list (a header name,
 *  a username). Webgen validates the kind — this map only decides which boxes
 *  the form draws, so a webgen that learns a new kind still lists it fine and
 *  only needs a line here to be writable from this page.
 */
const CONNECTION_KINDS = [
  {
    value: "bearer",
    label: "Bearer token",
    hint: "Sent as `Authorization: Bearer …`.",
    fields: ["secret"] as const,
  },
  {
    value: "api_key",
    label: "API key in a header",
    hint: "A key sent under a header you name.",
    fields: ["header", "secret"] as const,
  },
  {
    value: "basic",
    label: "Username + password",
    hint: "HTTP basic auth.",
    fields: ["username", "password"] as const,
  },
  {
    // webgen's kind is `header_set` — "header" was refused by it.
    value: "header_set",
    label: "Custom header",
    hint: "One header, name and value.",
    fields: ["header", "secret"] as const,
  },
  // ── F8: the kinds where webgen does the work, not the app ──────────────
  // Everything above is a static credential the proxy attaches to a request.
  // These three are a PROTOCOL: webgen fetches or signs per call, refreshes
  // before expiry, and reports `failing` when it cannot. The app's code is
  // identical either way — `ctx.egress.fetch(name, …)` — which is the whole
  // reason they belong here rather than in somebody's handler.
  {
    value: "oauth2_client_credentials",
    label: "OAuth2 — client credentials",
    hint: "Machine-to-machine. Chaidojo fetches a token and refreshes it.",
    fields: [
      "token_url", "client_id", "scope", "audience", "auth_style", "secret",
      "token_header", "token_query", "token_method",
    ] as const,
  },
  {
    value: "oauth2_refresh",
    label: "OAuth2 — refresh token",
    hint: "A token obtained once, on behalf of an account, and kept alive — paste it, or set a consent page and press Authorize on the row to sign in at the vendor.",
    fields: [
      "token_url", "client_id", "scope", "authorize_url", "authorize_params", "refresh_token",
      "secret", "auth_style", "token_header", "token_query", "token_method",
    ] as const,
  },
  {
    value: "oauth2_password",
    label: "OAuth2 — username and password",
    hint: "The password grant (an order system's API user): Chaidojo signs in for a token, and signs in again when it runs out.",
    fields: [
      "token_url", "client_id", "credentials_in", "token_method", "username", "password",
      "token_header", "token_query",
    ] as const,
  },
  {
    value: "aws_sigv4",
    label: "AWS SigV4",
    hint: "Requests are signed per call for one region and service.",
    fields: ["region", "service", "access_key_id", "secret"] as const,
  },
  {
    // webgen 2.19.0: a key that is never sent anywhere — no base URL, no egress.
    value: "hmac_key",
    label: "Hashing key",
    hint: "Never sent anywhere: ctx.keys.hash turns phone numbers and emails into stable keys inside Chaidojo, so the app stores keys, never the numbers.",
    fields: ["key"] as const,
  },
  // There is no "no credential" connection: an app calls a public URL with
  // `ctx.egress.fetchUrl`, its host in `egress:` (webgen refused the old one).
];

/** Boxes every kind but a header set gets, below its own (webgen 2.17.0):
 *  headers the vendor wants on every call — plain ones shown back, secret
 *  ones write-only. */
const EXTRA_HEADER_FIELDS = ["extra_headers", "secret_headers"] as const;

/** Kinds with no base URL and nothing sent: no headers, no call list. */
const KEY_KINDS = new Set(["hmac_key"]);

/** `POST services/rest/v1/export/job/create` a line → webgen's `config.allow`. */
function allowLines(text: string): { method: string; path_prefix: string }[] {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [method = "", ...rest] = l.split(/\s+/);
      return { method: method.toUpperCase(), path_prefix: rest.join(" ").replace(/^\/+/, "") };
    });
}

/** Which config field goes with which box, and how to label it. Kept beside
 *  CONNECTION_KINDS so adding a kind is two lines in one place. */
const CONNECTION_FIELDS: Record<
  string,
  { label: string; hint?: string; placeholder?: string; secret?: boolean; multiline?: boolean }
> = {
  header: {
    label: "Header name",
    hint: "The non-secret half — shown on the list.",
    placeholder: "X-API-Key",
  },
  username: { label: "Username" },
  secret: { label: "Value", secret: true },
  password: { label: "Password", secret: true },
  token_url: { label: "Token URL", placeholder: "https://login.example.com/oauth2/token" },
  client_id: { label: "Client ID" },
  scope: { label: "Scope", hint: "Optional — space-separated." },
  audience: { label: "Audience", hint: "Optional." },
  auth_style: {
    label: "Credential goes in",
    hint: "Optional — `basic` (the Authorization header) or `body`, whichever the provider documents.",
    placeholder: "basic",
  },
  token_header: {
    label: "Token goes in header",
    hint: "Optional — a header the API names, sent bare (x-amz-access-token). Empty: Authorization: Bearer.",
    placeholder: "Authorization",
  },
  token_query: {
    label: "…or in query parameter",
    hint: "Optional — for an API that takes the token in the URL (access_token).",
    placeholder: "access_token",
  },
  token_method: {
    label: "Token endpoint method",
    hint: "Optional — POST (the standard) or GET, for an endpoint that only answers GET.",
    placeholder: "POST",
  },
  extra_headers: {
    label: "Extra headers",
    hint: "Optional — one `Name: value` a line, on every call (a client or application id). Not secret.",
    placeholder: "Amazon-Advertising-API-ClientId: amzn1.application-oa2-client.…",
    multiline: true,
  },
  secret_headers: {
    label: "Secret extra headers",
    hint: "Optional — one `Name: value` a line (a developer token). Write-only.",
    placeholder: "developer-token: …",
    secret: true,
    multiline: true,
  },
  refresh_token: {
    label: "Refresh token",
    hint: "Leave empty when a consent page is set: Authorize obtains it.",
    secret: true,
  },
  authorize_url: {
    label: "Consent page",
    hint: "Optional — authorize here instead of pasting a refresh token. Amazon seller: https://sellercentral.amazon.in/apps/authorize/consent",
    placeholder: "https://sellercentral.amazon.in/apps/authorize/consent",
  },
  authorize_params: {
    label: "Consent parameters",
    hint: "Optional — one name=value a line, added to the consent link (Amazon: application_id, and version=beta while the app is a draft). The state and the callback are added by Chaidojo.",
    placeholder: "application_id=amzn1.sp.solution.…\nversion=beta",
    multiline: true,
  },
  credentials_in: {
    label: "Username and password go in",
    hint: "Optional — `body` (the standard) or `headers` (as username: and password: headers, for a vendor that documents it to keep the password out of URLs).",
    placeholder: "body",
  },
  key: {
    label: "Key",
    hint: "32 characters or more — the key the history was keyed with, so the keys join.",
    secret: true,
  },
  allow_calls: {
    label: "Only these calls",
    hint: "Optional — one `METHOD path` a line, under the base URL. Anything else is refused before it leaves, so a read-only integration cannot write. Empty: every call the app makes.",
    placeholder: "POST services/rest/v1/export/job/create",
    multiline: true,
  },
  region: { label: "Region", placeholder: "us-east-1" },
  service: { label: "Service", placeholder: "s3" },
  access_key_id: { label: "Access key ID" },
};

function millis(n: number | null | undefined): string {
  if (n == null) return "—";
  return n < 1000 ? `${n}ms` : `${(n / 1000).toFixed(1)}s`;
}

function when(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : formatDistanceToNow(d, { addSuffix: true });
}

function bytes(n: number | null | undefined): string {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Copy a secret WITHOUT echoing it. The ordinary `copy` puts the value in
 *  the toast description, which would leave a rotated signing secret on screen
 *  after the dialog that promised "this is the only time" has closed. */
async function copySecret(text: string, label = "Signing secret") {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  } catch {
    toast.error("Couldn't copy — select it and copy it by hand.");
  }
}

async function copy(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`, { description: text });
  } catch {
    toast.message(label, { description: text, duration: 10000 });
  }
}

function Addressed({ label, url }: { label: string; url: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-3 py-2">
      <span className="w-20 shrink-0 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </span>
      <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-[var(--color-slate)]">
        {url}
      </span>
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label={`Copy ${label} URL`}
        onClick={() => void copy(url, `${label} URL`)}
      >
        <Copy className="size-3 text-muted-foreground" />
      </Button>
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label={`Open ${label} URL`}
        onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
      >
        <ExternalLink className="size-3 text-muted-foreground" />
      </Button>
    </div>
  );
}

function AppDetailPage() {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isDemo = useIsDemoManage();
  const { id } = useParams({ from: "/manage/apps/$id" });
  const [tab, setTab] = useState("overview");
  const [autoRefresh, setAutoRefresh] = useState(false);
  /** Which delivery the drawer is showing (webgen's delivery id). */
  const [openDelivery, setOpenDelivery] = useState<string | null>(null);
  /** Page-forward only: webgen's cursor is the last row's id, so "older" is
   *  cheap and "newer" is just going back to the top. An operator chasing a
   *  delivery walks backwards in time and then stops. */
  const [deliveryCursor, setDeliveryCursor] = useState<string | null>(null);
  /** A just-rotated signing secret. Held in component state ONLY — this is
   *  the one and only time it exists in readable form (webgen keeps a hint,
   *  not the value), so it is never written to a query cache, a URL, or
   *  anywhere a refetch could try to get it back. */
  const [rotated, setRotated] = useState<{ path: string; secret: string; url: string } | null>(
    null,
  );
  /** The connection the credential dialog is editing, or null when closed.
   *  A name that already exists is an UPDATE (webgen replaces); a blank one
   *  is new. Nothing about the stored credential is ever preloaded — there is
   *  no read-back — so an update always retypes the secret. */
  const [editing, setEditing] = useState<Connection | "new" | null>(null);
  /** Which connection's rotation history is open (its name), or null. */
  const [historyOf, setHistoryOf] = useState<string | null>(null);
  /** The last Test result, by connection name. Not a query: a test is an ACT
   *  with a real call behind it, so it must never be refetched by a remount. */
  const [tested, setTested] = useState<Record<string, ConnectionTest>>({});
  /** True while the "new key" dialog is open. */
  const [mintingKey, setMintingKey] = useState(false);
  /** A just-minted key. Component state ONLY — this is the one and only time
   *  it exists in readable form (webgen keeps a prefix and a hash), so it is
   *  never written to a query cache, a URL, or anywhere a refetch could try to
   *  get it back. The chat card is the other half of this rule. */
  const [minted, setMinted] = useState<{ name: string; key: string } | null>(null);

  const detailKey = ["admin-webgen-app", id] as const;

  const app = useQuery({
    queryKey: detailKey,
    enabled: !isDemo,
    queryFn: () => api.request<AppDetail>(`/admin/webgen/apps/${id}`),
    retry: (count, err) =>
      !(err instanceof ApiError && [403, 404].includes(err.status)) && count < 2,
  });

  const versions = useQuery({
    queryKey: ["admin-webgen-app-versions", id],
    enabled: !isDemo && !!app.data,
    queryFn: () => api.request<AppVersion[]>(`/admin/webgen/apps/${id}/versions`),
    retry: false,
  });

  const stats = useQuery({
    queryKey: ["admin-webgen-app-stats", id],
    enabled: !isDemo && !!app.data,
    queryFn: () => api.request<AppStats>(`/admin/webgen/apps/${id}/stats`),
    retry: false,
  });

  const events = useQuery({
    queryKey: ["admin-webgen-app-events", id],
    enabled: !isDemo && !!app.data,
    queryFn: () => api.request<{ events: AppEvent[] }>(`/admin/webgen/apps/${id}/events?limit=50`),
    retry: false,
  });

  const logs = useQuery({
    queryKey: ["admin-webgen-app-logs", id],
    // Only while the tab is open: a log tail nobody is reading is a request
    // against a container every few seconds for nothing.
    enabled: !isDemo && tab === "logs",
    queryFn: () => api.request<AppLogs>(`/admin/webgen/apps/${id}/logs?tail=${LOG_TAIL}`),
    refetchInterval: autoRefresh ? 5000 : false,
    retry: false,
  });

  // ── Phase D: the webhook inbox and the schedule history ──────────────────
  // Both are tab-gated for the same reason the log tail is: polling a history
  // nobody is looking at is a request against webgen every few seconds for
  // nothing. `retry: false` because a 404 here is a state (this deployment's
  // webgen has no integrations lane yet), not a blip worth three attempts.

  const deliveries = useQuery({
    queryKey: ["admin-webgen-app-deliveries", id, deliveryCursor ?? ""],
    enabled: !isDemo && tab === "deliveries",
    queryFn: () =>
      api.request<{ deliveries: Delivery[]; next_cursor: string | null }>(
        `/admin/webgen/apps/${id}/deliveries?limit=${DELIVERY_LIMIT}${
          deliveryCursor ? `&cursor=${encodeURIComponent(deliveryCursor)}` : ""
        }`,
      ),
    retry: false,
  });

  const delivery = useQuery({
    queryKey: ["admin-webgen-app-delivery", id, openDelivery],
    enabled: !isDemo && !!openDelivery,
    queryFn: () =>
      api.request<DeliveryDetail>(
        `/admin/webgen/apps/${id}/deliveries/${encodeURIComponent(openDelivery!)}`,
      ),
    retry: false,
  });

  // The file inbox — what was POSTed to the ingest door and whether the
  // data room took it. Tab-gated like the deliveries.
  const ingests = useQuery({
    queryKey: ["admin-webgen-app-ingests", id],
    enabled: !isDemo && tab === "files",
    queryFn: () =>
      api.request<{ ingests: Ingest[]; next_cursor: string | null }>(
        `/admin/webgen/apps/${id}/ingests?limit=${DELIVERY_LIMIT}`,
      ),
  });

  const runs = useQuery({
    queryKey: ["admin-webgen-app-runs", id],
    enabled: !isDemo && tab === "schedules",
    queryFn: () =>
      api.request<{ runs: AppRun[] }>(`/admin/webgen/apps/${id}/runs?limit=${RUN_LIMIT}`),
    retry: false,
  });

  // Enabled on Overview too: the summary line there is the whole point of
  // fetching this cheaply — an operator should learn that a credential is
  // still pending without going looking for the tab.
  const connections = useQuery({
    queryKey: ["admin-webgen-app-connections", id],
    enabled: !isDemo && (tab === "credentials" || tab === "overview"),
    queryFn: () =>
      api.request<{ connections: Connection[] }>(`/admin/webgen/apps/${id}/connections`),
    retry: false,
  });

  // Tab-gated like the histories: nobody looking, nobody polling. `retry:
  // false` because a 404 is a state (this webgen has no keys lane yet), not a
  // blip worth three attempts.
  const keys = useQuery({
    queryKey: ["admin-webgen-app-keys", id],
    enabled: !isDemo && tab === "keys",
    queryFn: () => api.request<{ keys: AppKey[] }>(`/admin/webgen/apps/${id}/keys`),
    retry: false,
  });

  // Only for the connection whose History disclosure is open — a rotation
  // record nobody expanded is a request per row for nothing.
  const history = useQuery({
    queryKey: ["admin-webgen-connection-history", id, historyOf],
    enabled: !isDemo && !!historyOf,
    queryFn: () =>
      api.request<{ versions: ConnectionVersion[] }>(
        `/admin/webgen/apps/${id}/connections/${encodeURIComponent(historyOf!)}/history`,
      ),
    retry: false,
  });

  // Overview, always: an approval is the difference between an app that can
  // spend model budget and one that cannot, so it belongs beside the
  // capability it gates rather than behind a tab.
  const approvals = useQuery({
    queryKey: ["admin-webgen-app-approvals", id],
    enabled: !isDemo && !!app.data,
    queryFn: () =>
      api.request<{ approvable: string[]; approvals: Approval[] }>(
        `/admin/webgen/apps/${id}/approvals`,
      ),
    retry: false,
  });

  const workflowRuns = useQuery({
    queryKey: ["admin-webgen-app-workflow-runs", id],
    enabled: !isDemo && !!app.data,
    queryFn: () =>
      api.request<{ runs: AppWorkflowRun[] }>(
        `/admin/webgen/apps/${id}/workflow-runs?limit=${RUN_LIMIT}`,
      ),
    retry: false,
  });

  const setApproval = useMutation({
    mutationFn: ({
      capability,
      approve,
    }: {
      capability: string;
      approve: boolean;
    }) =>
      api.request<{ ok: boolean }>(
        `/admin/webgen/apps/${id}/approvals/${encodeURIComponent(capability)}`,
        approve
          ? {
              method: "PUT",
              body: JSON.stringify({
                workspace_id: app.data?.workspace_id ?? "",
                app_slug: app.data?.slug ?? "",
              }),
            }
          : { method: "DELETE" },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: ["admin-webgen-app-approvals", id],
      });
    },
  });

  const putConnection = useMutation({
    mutationFn: (body: {
      name: string;
      kind: string;
      base_url: string;
      config: Record<string, unknown>;
      secret?: Record<string, unknown>;
    }) =>
      api.request<{ name: string }>(
        `/admin/webgen/apps/${id}/connections/${encodeURIComponent(body.name)}`,
        {
          method: "PUT",
          body: JSON.stringify({
            kind: body.kind,
            base_url: body.base_url,
            config: body.config,
            ...(body.secret ? { secret: body.secret } : {}),
          }),
        },
      ),
    onSuccess: (_r, body) => {
      setEditing(null);
      toast.success(`"${body.name}" saved`, {
        description: "The app can use it on its next request.",
      });
    },
    onError: (err: { message?: string }) => toast.error(err.message ?? "Save failed"),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["admin-webgen-app-connections", id] });
    },
  });

  const deleteConnection = useMutation({
    mutationFn: (name: string) =>
      api.request<{ name: string }>(
        `/admin/webgen/apps/${id}/connections/${encodeURIComponent(name)}`,
        { method: "DELETE" },
      ),
    onSuccess: (_r, name) =>
      toast.success(`"${name}" removed`, {
        description: "Calls to it will now fail until it is set again.",
      }),
    onError: (err: { message?: string }) => toast.error(err.message ?? "Delete failed"),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["admin-webgen-app-connections", id] });
    },
  });

  // A test is not a read: it makes one real call, with the stored credential,
  // to somebody else's API. So it is a mutation with a button in front of it,
  // and its result is held rather than cached.
  const testConnection = useMutation({
    mutationFn: (name: string) =>
      api.request<ConnectionTest>(
        `/admin/webgen/apps/${id}/connections/${encodeURIComponent(name)}/test`,
        { method: "POST" },
      ),
    onSuccess: (r, name) => {
      setTested((prev) => ({ ...prev, [name]: r }));
      if (r?.ok) {
        toast.success(`"${name}" answered`, {
          description: `${r.status ?? 200} in ${millis(r.took_ms)}.`,
        });
      } else {
        toast.error(`"${name}" failed`, { description: r?.error ?? "No answer." });
      }
    },
    onError: (err: { message?: string }, name) => {
      setTested((prev) => ({ ...prev, [name]: { ok: false, error: err.message ?? "failed" } }));
      toast.error(err.message ?? "Couldn't test that connection");
    },
    // The attempt itself is what moves a connection between `configured` and
    // `failing`, so re-read the list either way.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["admin-webgen-app-connections", id] });
    },
  });

  // W5: Authorize opens the vendor's consent page in a popup. The callback page
  // posts its result back to this window (same origin), and the row re-reads.
  const authorizeConnection = useMutation({
    mutationFn: (name: string) =>
      api.request<{ url: string }>(
        `/admin/webgen/apps/${id}/connections/${encodeURIComponent(name)}/authorize`,
        { method: "POST" },
      ),
    onSuccess: (r) => {
      const popup = window.open(r.url, "chai-authorize", "popup,width=640,height=780");
      if (!popup) window.location.assign(r.url);
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? "Couldn't start the authorization");
    },
  });
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const d = e.data as { type?: string; ok?: boolean; message?: string } | null;
      if (!d || d.type !== "connectors-oauth") return;
      if (d.ok) toast.success(d.message ?? "Authorized");
      else toast.error(d.message ?? "Not authorized");
      void qc.invalidateQueries({ queryKey: ["admin-webgen-app-connections", id] });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [id, qc]);

  const mintKey = useMutation({
    mutationFn: (body: { name: string; scopes: KeyScope[]; rate_rpm?: number | null }) =>
      api.request<AppKey & { key: string }>(`/admin/webgen/apps/${id}/keys`, {
        method: "POST",
        body: JSON.stringify({
          name: body.name,
          scopes: body.scopes,
          ...(body.rate_rpm != null ? { rate_rpm: body.rate_rpm } : {}),
        }),
      }),
    // Straight into the dialog, like a rotated signing secret: the response is
    // the only time the value is readable, so it is shown in the same breath
    // it arrives and never travels anywhere else.
    onSuccess: (r) => {
      setMintingKey(false);
      setMinted({ name: r.name, key: r.key });
    },
    onError: (err: { message?: string }) => toast.error(err.message ?? "Couldn't mint that key"),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["admin-webgen-app-keys", id] }),
  });

  const revokeKey = useMutation({
    mutationFn: (keyId: string) =>
      api.request<{ id: string }>(`/admin/webgen/apps/${id}/keys/${encodeURIComponent(keyId)}`, {
        method: "DELETE",
      }),
    onSuccess: () =>
      toast.success("Key revoked", {
        description: "Anything calling with it starts getting 401s now.",
      }),
    onError: (err: { message?: string }) => toast.error(err.message ?? "Revoke failed"),
    onSettled: () => void qc.invalidateQueries({ queryKey: ["admin-webgen-app-keys", id] }),
  });

  const replay = useMutation({
    mutationFn: (deliveryId: string) =>
      api.request<{ id: string; status: string }>(
        `/admin/webgen/apps/${id}/deliveries/${encodeURIComponent(deliveryId)}/replay`,
        { method: "POST" },
      ),
    onSuccess: (r) =>
      toast.success("Replayed", {
        description: `The handler ran again — ${r?.status ?? "sent"}.`,
      }),
    onError: (err: { message?: string }) => toast.error(err.message ?? "Replay failed"),
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: ["admin-webgen-app-deliveries", id],
      });
      void qc.invalidateQueries({ queryKey: ["admin-webgen-app-delivery", id] });
    },
  });

  // Webgen runs the cron THERE AND THEN and answers with the verdict, so the
  // toast says what happened rather than "started" — a failed run that
  // reported success would be the worst of both.
  const runCron = useMutation({
    mutationFn: (name: string) =>
      api.request<{ run_id: string; status: string; error: string | null }>(
        `/admin/webgen/apps/${id}/crons/${encodeURIComponent(name)}/run`,
        { method: "POST" },
      ),
    onSuccess: (r, name) =>
      r?.status === "error"
        ? toast.error(`"${name}" failed`, {
            description: r.error ?? "See the run history.",
          })
        : toast.success(`"${name}" ran`, {
            description: `Finished ${r?.status ?? "ok"} — it's in the run history.`,
          }),
    onError: (err: { message?: string }) =>
      toast.error(err.message ?? "Couldn't start that schedule"),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["admin-webgen-app-runs", id] });
      void qc.invalidateQueries({ queryKey: detailKey });
    },
  });

  const rotate = useMutation({
    mutationFn: (path: string) =>
      api.request<{ signing_secret: string; url: string }>(
        `/admin/webgen/apps/${id}/webhooks/${encodeURIComponent(path)}/rotate`,
        { method: "POST" },
      ),
    // Straight into the dialog: the response is the only time the secret is
    // readable, so it is shown in the same breath it arrives.
    onSuccess: (r, path) => setRotated({ path, secret: r.signing_secret, url: r.url }),
    onError: (err: { message?: string }) => toast.error(err.message ?? "Rotation failed"),
    onSettled: () => void qc.invalidateQueries({ queryKey: detailKey }),
  });

  const refreshAll = () => {
    void qc.invalidateQueries({ queryKey: detailKey });
    void qc.invalidateQueries({ queryKey: ["admin-webgen-app-versions", id] });
    void qc.invalidateQueries({ queryKey: ["admin-webgen-app-stats", id] });
    void qc.invalidateQueries({ queryKey: ["admin-webgen-app-events", id] });
    void qc.invalidateQueries({ queryKey: ["admin-webgen-app-deliveries", id] });
    void qc.invalidateQueries({ queryKey: ["admin-webgen-app-runs", id] });
    void qc.invalidateQueries({ queryKey: APPS_KEY });
  };

  const setPaused = useMutation({
    mutationFn: (paused: boolean) =>
      api.request<WebgenAppRow>(`/admin/webgen/apps/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ paused }),
      }),
    onSuccess: (_d, paused) => toast.success(paused ? "App paused" : "App resumed"),
    onError: (err: { message?: string }) =>
      toast.error(err.message ?? "Couldn't change the app's state"),
    onSettled: refreshAll,
  });

  const restart = useMutation({
    mutationFn: () =>
      api.request<WebgenAppRow>(`/admin/webgen/apps/${id}/restart`, {
        method: "POST",
      }),
    onSuccess: () => toast.success("Restarted — the next request wakes a fresh container"),
    onError: (err: { message?: string }) => toast.error(err.message ?? "Restart failed"),
    onSettled: refreshAll,
  });

  const rollback = useMutation({
    mutationFn: (n: number) =>
      api.request<{ ok: boolean; version: number; health: string }>(
        `/admin/webgen/apps/${id}/versions/${n}/rollback`,
        { method: "POST" },
      ),
    onSuccess: (r, n) =>
      r?.ok
        ? toast.success(`Rolled back to v${n} — now live as v${r.version}`)
        : toast.error(`Roll back to v${n} failed its health check`),
    onError: (err: { message?: string }) => toast.error(err.message ?? "Roll back failed"),
    onSettled: refreshAll,
  });

  const archive = useMutation({
    mutationFn: () => api.request<WebgenAppRow>(`/admin/webgen/apps/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("App archived — its bundle and data are kept");
      void qc.invalidateQueries({ queryKey: APPS_KEY });
      void navigate({ to: "/manage/apps" });
    },
    onError: (err: { message?: string }) => toast.error(err.message ?? "Archive failed"),
  });

  const busy = setPaused.isPending || restart.isPending || rollback.isPending || archive.isPending;

  if (isDemo) {
    return (
      <div className="mx-auto max-w-[960px] px-10 py-10 text-sm text-muted-foreground">
        Not available in this demo.
      </div>
    );
  }

  if (app.isLoading) {
    return (
      <div className="mx-auto max-w-[960px] px-10 py-10">
        <Skeleton className="h-48 w-full rounded-md" />
      </div>
    );
  }

  if (app.isError || !app.data) {
    return (
      <div className="mx-auto max-w-[960px] px-10 py-10">
        <BackLink />
        <div className="mt-6 rounded-lg border border-dashed border-[var(--color-hairline)] bg-card px-4 py-10 text-center text-sm text-muted-foreground">
          {(app.error as { message?: string })?.message ?? "That app isn't there any more."}
        </div>
      </div>
    );
  }

  const a = app.data;
  const paused = a.status === "paused";
  const rows = versions.data ?? a.versions ?? [];

  return (
    <div className="mx-auto max-w-[960px] px-10 py-10">
      <BackLink />
      <PageHeader
        eyebrow="Admin · Publishing · App"
        title={a.title}
        tabTitle={[a.title, "Apps"]}
        description={a.blueprint ? `Built from ${a.blueprint}.` : undefined}
        meta={
          <>
            <code className="rounded bg-[var(--color-surface-soft)] px-1.5 py-0.5 font-mono text-[11px]">
              {a.slug}
            </code>
            <StatusBadge label={a.status} tone={APP_STATUS_TONE[a.status] ?? "neutral"} />
            <StatusBadge label={a.access} tone={ACCESS_TONE[a.access] ?? "neutral"} />
            {/* A service app (any schedule) is pinned warm and never idles, so
                it is a standing cost on the box — worth a chip beside the
                status rather than a line somebody has to go and find. */}
            {a.service && (
              <span title="This app declares a schedule, so it is pinned warm: it never idles, it is restarted if it exits, and it counts against this workspace's service-app quota. Pause it to free the slot.">
                <StatusBadge label="service" tone="info" />
              </span>
            )}
            <span>{a.service ? "always on" : a.warm ? "container up" : "cold"}</span>
            <span>
              last activity{" "}
              {a.last_activity
                ? formatDistanceToNow(new Date(a.last_activity), { addSuffix: true })
                : "never"}
            </span>
          </>
        }
        actions={
          <Button variant="outline" size="sm" onClick={refreshAll} disabled={busy}>
            <RefreshCw className="size-3.5" /> Refresh
          </Button>
        }
      />

      {a.error && (
        <div className="mb-8 rounded-lg border border-[var(--color-hairline)] bg-[var(--color-card-tint-rose)] px-4 py-3 text-sm text-[var(--color-semantic-error)]">
          {a.error}
        </div>
      )}

      {/* `flex-col` explicitly: the Tabs root ships `data-horizontal:flex-col`,
          which never matches (base-ui stamps `data-orientation`), so without
          this the tab list and the panel sit SIDE BY SIDE. */}
      <Tabs value={tab} onValueChange={(v) => setTab(String(v))} className="flex-col items-stretch">
        <TabsList variant="line" className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="deliveries">Deliveries</TabsTrigger>
          <TabsTrigger value="files">Files</TabsTrigger>
          <TabsTrigger value="schedules">Schedules</TabsTrigger>
          <TabsTrigger value="credentials">Credentials</TabsTrigger>
          <TabsTrigger value="keys">API keys</TabsTrigger>
          <TabsTrigger value="people">People</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
          <TabsTrigger value="storage">Storage</TabsTrigger>
          <TabsTrigger value="danger">Danger zone</TabsTrigger>
        </TabsList>

        {/* ── Overview ─────────────────────────────────────────────────── */}
        <TabsContent value="overview">
          <Section
            title="Addresses"
            description="The share link is what a person is given; the app URL is what an iframe loads."
          >
            <div className="flex flex-col gap-2">
              <Addressed label="Share" url={a.share_url ?? a.url} />
              <Addressed label="App" url={a.url} />
            </div>
            {a.routes?.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-1.5">
                {a.routes.map((r) => (
                  <li
                    key={r}
                    className="rounded bg-[var(--color-surface-soft)] px-2 py-0.5 font-mono text-[11px] text-muted-foreground"
                  >
                    {r}
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <WebhooksSection
            webhooks={a.webhooks}
            rotating={rotate.isPending ? (rotate.variables ?? null) : null}
            onRotate={(path) => {
              if (
                confirm(
                  `Rotate the signing secret for "${path}"?\n\nThe sender stops being accepted until you give it the new secret, and the new secret is shown once — there is no way to read it again.`,
                )
              ) {
                rotate.mutate(path);
              }
            }}
          />

          <ConnectionsSummary
            connections={connections.data?.connections}
            onOpen={() => setTab("credentials")}
          />

          <CapabilitiesSection
            cd={a.capabilities}
            approvable={approvals.data?.approvable}
            approvals={approvals.data?.approvals}
            pending={setApproval.isPending ? (setApproval.variables?.capability ?? null) : null}
            onApprove={(capability, approve) => setApproval.mutate({ capability, approve })}
          />

          <AppWorkflowRunsSection runs={workflowRuns.data?.runs} />

          <Section
            title="Versions"
            description="Every deploy is kept. Rolling back re-provisions from a stored bundle and lands as a NEW version — the history is append-only."
          >
            <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
              <table className="w-full text-sm">
                <thead className="bg-[var(--color-surface-soft)]">
                  <tr className="text-left">
                    <th className="w-16 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                      Ver
                    </th>
                    <th className="w-24 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                      Health
                    </th>
                    <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                      Notes
                    </th>
                    <th className="w-28 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                      Deployed
                    </th>
                    <th className="w-28 px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                  {rows.length ? (
                    rows.map((v) => {
                      const live = v.live ?? v.version === a.version;
                      return (
                        <tr key={v.version}>
                          <td className="px-4 py-3 font-mono text-xs tabular-nums">
                            v{v.version}
                            {live && (
                              <span className="ml-1.5 font-sans text-[10px] uppercase tracking-wider text-[var(--color-semantic-success)]">
                                live
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge
                              label={v.health ?? "unknown"}
                              tone={v.health === "healthy" ? "ready" : "failed"}
                            />
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {v.notes ?? "—"}
                            {v.bytes?.code != null && (
                              <span className="ml-2 font-mono text-[11px]">
                                {bytes((v.bytes.code ?? 0) + (v.bytes.static ?? 0))}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(v.created_at), {
                              addSuffix: true,
                            })}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {!live && (
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={busy}
                                onClick={() => {
                                  if (
                                    confirm(
                                      `Roll back "${a.title}" to v${v.version}? It re-provisions from that bundle and becomes the live version.`,
                                    )
                                  ) {
                                    rollback.mutate(v.version);
                                  }
                                }}
                              >
                                <RotateCcw className="size-3.5" /> Roll back
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-4 py-8 text-center text-xs text-muted-foreground"
                      >
                        Never deployed — this app has a bundle but no live version yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Section>

          <Section
            title="Provisioning"
            description="What the reconciler did on the last deploy, step by step."
          >
            {stats.data?.provision_log?.length ? (
              <ol className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
                {stats.data.provision_log.map((s, i) => (
                  <li
                    key={`${s.step}-${i}`}
                    className="flex items-baseline gap-3 border-b border-[var(--color-hairline-soft)] px-4 py-2 last:border-b-0"
                  >
                    <span
                      className={`size-1.5 shrink-0 translate-y-[-1px] rounded-full ${
                        s.ok
                          ? "bg-[var(--color-semantic-success)]"
                          : "bg-[var(--color-semantic-error)]"
                      }`}
                      aria-hidden
                    />
                    <span className="w-32 shrink-0 font-mono text-[11px] font-medium">
                      {s.step}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                      {s.detail}
                    </span>
                    <span className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
                      {s.took_ms}ms
                    </span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="rounded-lg border border-dashed border-[var(--color-hairline)] bg-card px-4 py-8 text-center text-xs text-muted-foreground">
                No provisioning recorded yet.
              </p>
            )}
          </Section>

          <Section title="Events" description="Lifecycle and app-emitted, newest first.">
            {events.data?.events?.length ? (
              <ul className="max-h-80 overflow-y-auto rounded-lg border border-[var(--color-hairline)] bg-card">
                {events.data.events.map((e, i) => (
                  <li
                    key={`${e.at}-${i}`}
                    className="flex items-baseline gap-3 border-b border-[var(--color-hairline-soft)] px-4 py-2 last:border-b-0"
                  >
                    <span className="w-40 shrink-0 font-mono text-[10px] text-muted-foreground">
                      {new Date(e.at).toLocaleString()}
                    </span>
                    <span className="w-28 shrink-0 font-mono text-[11px] font-medium">
                      {e.kind}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                      {e.msg ?? ""}
                      {e.data && Object.keys(e.data).length > 0 && (
                        <code className="ml-2 font-mono text-[11px]">{JSON.stringify(e.data)}</code>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="rounded-lg border border-dashed border-[var(--color-hairline)] bg-card px-4 py-8 text-center text-xs text-muted-foreground">
                Nothing recorded yet.
              </p>
            )}
          </Section>
        </TabsContent>

        {/* ── Deliveries ───────────────────────────────────────────────── */}
        {/* The inbound webhook log. Two chips per row on purpose: `verdict`
            answers "was this really from them", `status` answers "did our
            handler cope". An operator chasing a missing event needs to know
            which of the two failed before anything else. */}
        <TabsContent value="deliveries">
          <Section
            title="Deliveries"
            description="What arrived at this app's webhook endpoints, newest first. Click a row to read the headers and body."
            actions={
              <Button
                variant="outline"
                size="sm"
                disabled={deliveries.isFetching}
                onClick={() => void deliveries.refetch()}
              >
                <RefreshCw className="size-3.5" /> Refresh
              </Button>
            }
          >
            {deliveries.isLoading ? (
              <Skeleton className="h-48 w-full rounded-md" />
            ) : deliveries.isError ? (
              <QuietNote>
                {isNotFound(deliveries.error)
                  ? "This deployment's webgen doesn't serve a webhook inbox yet."
                  : ((deliveries.error as { message?: string }).message ??
                    "Couldn't read the deliveries.")}
              </QuietNote>
            ) : (
              <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-surface-soft)]">
                    <tr className="text-left">
                      <Th className="w-28">Received</Th>
                      <Th>Endpoint</Th>
                      <Th className="w-28">Signature</Th>
                      <Th className="w-28">Status</Th>
                      <Th className="w-20">Tries</Th>
                      <Th className="w-24">Response</Th>
                      <Th className="w-24" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                    {deliveries.data?.deliveries?.length ? (
                      deliveries.data.deliveries.map((d) => (
                        <tr
                          key={d.id}
                          className="cursor-pointer hover:bg-[var(--color-row-hover)]"
                          onClick={() => setOpenDelivery(d.id)}
                        >
                          <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                            {when(d.received_at)}
                          </td>
                          <td className="px-4 py-3 font-mono text-[11px]">
                            {d.endpoint}
                            {d.last_error && (
                              <span className="ml-2 text-[var(--color-semantic-error)]">
                                {d.last_error}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge
                              label={d.verdict}
                              tone={VERDICT_TONE[d.verdict] ?? "neutral"}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge
                              label={d.status}
                              tone={DELIVERY_STATUS_TONE[d.status] ?? "neutral"}
                            />
                          </td>
                          <td className="px-4 py-3 font-mono text-xs tabular-nums text-muted-foreground">
                            {d.attempts}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs tabular-nums text-muted-foreground">
                            {d.response_status ?? "—"}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={replay.isPending}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  confirm(
                                    "Replay this delivery? The handler runs again, with whatever it does to your data.",
                                  )
                                ) {
                                  replay.mutate(d.id);
                                }
                              }}
                            >
                              <Redo2 className="size-3.5" /> Replay
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center">
                          <p className="text-xs text-muted-foreground">Nothing has arrived yet.</p>
                          <p className="mx-auto mt-1 max-w-prose text-xs text-muted-foreground">
                            Each webhook's inbound URL is on the{" "}
                            <button
                              type="button"
                              className="underline underline-offset-2"
                              onClick={() => setTab("overview")}
                            >
                              Overview
                            </button>{" "}
                            tab — that address is what the sender posts to, and it needs the signing
                            secret to be accepted.
                          </p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {(deliveries.data?.next_cursor || deliveryCursor) && (
              <div className="mt-2 flex items-center gap-2">
                {deliveryCursor && (
                  <Button variant="ghost" size="sm" onClick={() => setDeliveryCursor(null)}>
                    Back to newest
                  </Button>
                )}
                {deliveries.data?.next_cursor && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-auto"
                    onClick={() => setDeliveryCursor(deliveries.data?.next_cursor ?? null)}
                  >
                    Older
                  </Button>
                )}
              </div>
            )}
          </Section>
        </TabsContent>

        {/* ── Schedules ────────────────────────────────────────────────── */}
        {/* Two tables that answer different questions: what is SUPPOSED to
            happen (the declared crons, with next/last) and what DID (the run
            history). A cron with a healthy schedule and an empty history is
            the failure mode worth seeing at a glance. */}
        <TabsContent value="files">
          <Section
            title="Files"
            description="What was POSTed to this app's ingest URL, newest first, and whether the data room took each file."
            actions={
              <Button
                variant="outline"
                size="sm"
                disabled={ingests.isFetching}
                onClick={() => void ingests.refetch()}
              >
                <RefreshCw className="size-3.5" /> Refresh
              </Button>
            }
          >
            {ingests.isLoading ? (
              <Skeleton className="h-48 w-full rounded-md" />
            ) : ingests.isError ? (
              <QuietNote>
                {isNotFound(ingests.error)
                  ? "This app declares no ingest door."
                  : ((ingests.error as { message?: string }).message ??
                    "Couldn't read the file inbox.")}
              </QuietNote>
            ) : (
              <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-surface-soft)]">
                    <tr className="text-left">
                      <Th className="w-28">Received</Th>
                      <Th>File</Th>
                      <Th className="w-24">Size</Th>
                      <Th className="w-28">Handler</Th>
                      <Th className="w-28">Data room</Th>
                      <Th>Asset</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                    {ingests.data?.ingests?.length ? (
                      ingests.data.ingests.map((f) => (
                        <tr key={f.id}>
                          <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                            {when(f.received_at)}
                          </td>
                          <td className="px-4 py-3 font-mono text-[11px]">
                            {f.name}
                            {f.last_error && (
                              <span className="ml-2 text-[var(--color-semantic-error)]">
                                {f.last_error}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs tabular-nums text-muted-foreground">
                            {f.bytes.toLocaleString()} B
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge
                              label={f.status}
                              tone={DELIVERY_STATUS_TONE[f.status] ?? "neutral"}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge
                              label={f.forward ?? "—"}
                              tone={
                                f.forward === "ok"
                                  ? "ready"
                                  : f.forward === "failed"
                                    ? "failed"
                                    : "neutral"
                              }
                            />
                            {f.forward_error && (
                              <span className="ml-2 text-[11px] text-[var(--color-semantic-error)]">
                                {f.forward_error}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                            {f.forwarded_asset_id ? `${f.forwarded_asset_id.slice(0, 12)}…` : "—"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                          Nothing has been posted to this app's ingest URL yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="schedules">
          <Section
            title="Schedules"
            description="What this app is declared to run on a timer. The app declares these in its manifest; they can't be added from here."
          >
            <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
              <table className="w-full text-sm">
                <thead className="bg-[var(--color-surface-soft)]">
                  <tr className="text-left">
                    <Th>Name</Th>
                    <Th className="w-36">Schedule</Th>
                    <Th className="w-20">Budget</Th>
                    <Th className="w-28">Last run</Th>
                    <Th className="w-28">Next run</Th>
                    <Th className="w-24">State</Th>
                    <Th className="w-28" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                  {a.crons?.length ? (
                    a.crons.map((c) => (
                      <tr key={c.name}>
                        <td className="px-4 py-3 font-medium">{c.name}</td>
                        <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                          {c.schedule}
                        </td>
                        <td
                          className="px-4 py-3 font-mono text-[11px] text-muted-foreground"
                          title="Wall clock for one run of this schedule, from the manifest's budget_s."
                        >
                          {c.budget_s ? `${c.budget_s}s` : "—"}
                        </td>
                        <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                          {when(c.last_run_at)}
                        </td>
                        <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                          {c.error ? (
                            <span className="text-[var(--color-semantic-error)]">{c.error}</span>
                          ) : c.enabled ? (
                            when(c.next_run_at)
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge
                            label={c.enabled ? "enabled" : "paused"}
                            tone={c.enabled ? "ready" : "neutral"}
                          />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={runCron.isPending}
                            onClick={() => runCron.mutate(c.name)}
                          >
                            <Zap className="size-3.5" /> Run now
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-10 text-center text-xs text-muted-foreground"
                      >
                        {a.crons
                          ? "This app declares no schedules."
                          : "This deployment's webgen doesn't run schedules yet."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Section>

          <Section
            title="Run history"
            description="Newest first. “Skipped” means the previous run was still going — normal, not a fault."
            actions={
              <Button
                variant="outline"
                size="sm"
                disabled={runs.isFetching}
                onClick={() => void runs.refetch()}
              >
                <RefreshCw className="size-3.5" /> Refresh
              </Button>
            }
          >
            {runs.isLoading ? (
              <Skeleton className="h-40 w-full rounded-md" />
            ) : runs.isError ? (
              <QuietNote>
                {isNotFound(runs.error)
                  ? "This deployment's webgen doesn't keep a run history yet."
                  : ((runs.error as { message?: string }).message ??
                    "Couldn't read the run history.")}
              </QuietNote>
            ) : (
              <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-surface-soft)]">
                    <tr className="text-left">
                      <Th>Schedule</Th>
                      <Th className="w-24">Status</Th>
                      <Th className="w-28">Started</Th>
                      <Th className="w-24">Took</Th>
                      <Th>Error</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                    {runs.data?.runs?.length ? (
                      runs.data.runs.map((r) => (
                        <tr key={r.id}>
                          <td className="px-4 py-3 font-medium">{r.name}</td>
                          <td className="px-4 py-3">
                            <StatusBadge
                              label={r.status}
                              tone={RUN_STATUS_TONE[r.status] ?? "neutral"}
                            />
                          </td>
                          <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                            {when(r.started_at ?? r.scheduled_at)}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs tabular-nums text-muted-foreground">
                            {millis(r.duration_ms)}
                          </td>
                          <td className="px-4 py-3 text-xs text-[var(--color-semantic-error)]">
                            {r.error ?? ""}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-4 py-10 text-center text-xs text-muted-foreground"
                        >
                          Nothing has run yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </TabsContent>

        {/* ── Logs ─────────────────────────────────────────────────────── */}
        {/* ── Credentials ──────────────────────────────────────────────── */}
        <TabsContent value="credentials">
          <Section
            title="Connections"
            description="What this app is allowed to call, what it authenticates with, and whether that still works. The credential is write-only everywhere: webgen stores it, the app's code never holds it, and nothing here or on the agent's tool surface can read it back — Test makes a real call rather than showing you the value."
            actions={
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing("new")}
                disabled={putConnection.isPending}
              >
                <Plus className="size-3.5" /> Add connection
              </Button>
            }
          >
            {connections.isLoading ? (
              <Skeleton className="h-24 w-full rounded-lg" />
            ) : isNotFound(connections.error) ? (
              <QuietNote>This deployment's webgen doesn't have the connections lane yet.</QuietNote>
            ) : connections.isError ? (
              <QuietNote>Couldn't read the connections.</QuietNote>
            ) : !connections.data?.connections?.length ? (
              <QuietNote>
                No connections. The app reaches nothing outside itself until one is added — here, or
                by the assistant asking for it in the chat.
              </QuietNote>
            ) : (
              <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-surface-soft)]">
                    <tr className="text-left">
                      <Th className="w-40">Name</Th>
                      <Th className="w-36">Kind</Th>
                      <Th>Base URL</Th>
                      <Th className="w-36">Status</Th>
                      <Th className="w-28">Last used</Th>
                      <Th className="w-44" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                    {connections.data.connections.map((c) => (
                      <Fragment key={c.name}>
                        <tr>
                          <td className="px-4 py-3 align-top font-mono text-xs">{c.name}</td>
                          <td className="px-4 py-3 align-top text-xs text-muted-foreground">
                            {CONNECTION_KINDS.find((k) => k.value === c.kind)?.label ?? c.kind}
                            {configSummary(c) && (
                              <span className="ml-1.5 font-mono text-[11px]">
                                {configSummary(c)}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top font-mono text-[11px] text-muted-foreground">
                            <span className="break-all">{c.base_url || "—"}</span>
                          </td>
                          <td className="px-4 py-3 align-top">
                            <StatusBadge
                              label={c.status}
                              tone={CONNECTION_STATUS_TONE[c.status] ?? "neutral"}
                            />
                            <ConnectionHealth connection={c} test={tested[c.name]} />
                          </td>
                          <td className="px-4 py-3 align-top font-mono text-[11px] text-muted-foreground">
                            {when(c.last_used_at ?? null) === "—" ? "never" : when(c.last_used_at)}
                            <div className="text-[10px] text-muted-foreground/70">
                              set {when(c.updated_at)}
                            </div>
                          </td>
                          <td className="px-4 py-3 align-top">
                            <div className="flex items-center justify-end gap-1.5">
                              {c.kind === "oauth2_refresh" &&
                                typeof c.config?.authorize_url === "string" &&
                                c.config.authorize_url && (
                                  <Button
                                    variant={c.status === "unauthorized" ? "default" : "ghost"}
                                    size="sm"
                                    disabled={
                                      authorizeConnection.isPending &&
                                      authorizeConnection.variables === c.name
                                    }
                                    onClick={() => authorizeConnection.mutate(c.name)}
                                    title="Opens the vendor's consent page; sign in as the account this connection is for."
                                  >
                                    <LogIn className="size-3.5" />
                                    {c.status === "unauthorized" ? "Authorize" : "Re-authorize"}
                                  </Button>
                                )}
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={
                                  testConnection.isPending && testConnection.variables === c.name
                                }
                                onClick={() => testConnection.mutate(c.name)}
                                title="Makes one real call with the stored credential."
                              >
                                {testConnection.isPending && testConnection.variables === c.name ? (
                                  <RefreshCw className="size-3.5 animate-spin" />
                                ) : (
                                  <Zap className="size-3.5" />
                                )}
                                Test
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditing(c)}
                                disabled={putConnection.isPending}
                              >
                                <KeyRound className="size-3.5" /> Set
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={
                                  deleteConnection.isPending &&
                                  deleteConnection.variables === c.name
                                }
                                onClick={() => {
                                  if (
                                    confirm(
                                      `Remove the "${c.name}" connection?\n\nThe credential is deleted and cannot be recovered — every call the app makes to ${c.base_url || "it"} will fail until it is set again.`,
                                    )
                                  ) {
                                    deleteConnection.mutate(c.name);
                                  }
                                }}
                              >
                                <Trash2 className="size-3.5" />
                              </Button>
                            </div>
                            <button
                              type="button"
                              className="mt-1 w-full text-right text-[11px] text-muted-foreground hover:text-foreground"
                              onClick={() => setHistoryOf(historyOf === c.name ? null : c.name)}
                            >
                              {historyOf === c.name ? "Hide history" : "History"}
                              {c.secret_version ? ` (v${c.secret_version})` : ""}
                            </button>
                          </td>
                        </tr>
                        {historyOf === c.name && (
                          <tr>
                            <td colSpan={6} className="bg-[var(--color-surface-soft)] px-4 py-3">
                              <ConnectionHistory
                                versions={history.data?.versions}
                                loading={history.isLoading}
                                error={history.isError ? history.error : null}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </TabsContent>

        {/* ── API keys ─────────────────────────────────────────────────── */}
        <TabsContent value="keys">
          <Section
            title="API keys"
            description="What other systems call THIS app with — a partner's script, another team's job. The reverse of a connection, and the one place a value is ever shown: once, in the dialog that mints it. After that only the prefix exists here."
            actions={
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMintingKey(true)}
                disabled={mintKey.isPending}
              >
                <Plus className="size-3.5" /> New key
              </Button>
            }
          >
            {keys.isLoading ? (
              <Skeleton className="h-24 w-full rounded-lg" />
            ) : isNotFound(keys.error) ? (
              <QuietNote>This deployment's webgen doesn't have the API-keys lane yet.</QuietNote>
            ) : keys.isError ? (
              <QuietNote>Couldn't read the keys.</QuietNote>
            ) : !keys.data?.keys?.length ? (
              <QuietNote>
                No keys. Nothing outside Chaidojo can call this app's API — mint one here, or ask
                the assistant for one in the chat and the key appears on a card there.
              </QuietNote>
            ) : (
              <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-surface-soft)]">
                    <tr className="text-left">
                      <Th className="w-40">Name</Th>
                      <Th className="w-36">Prefix</Th>
                      <Th>Can call</Th>
                      <Th className="w-20">Rate</Th>
                      <Th className="w-28">Last used</Th>
                      <Th className="w-20">Calls</Th>
                      <Th className="w-24" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                    {keys.data.keys.map((k) => (
                      <tr key={k.id} className={k.revoked_at ? "opacity-55" : undefined}>
                        <td className="px-4 py-3 align-top">
                          <div className="font-mono text-xs">{k.name}</div>
                          {k.revoked_at && (
                            <div className="mt-1 text-[11px] text-muted-foreground">
                              revoked {when(k.revoked_at)}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top font-mono text-[11px] text-muted-foreground">
                          {k.prefix ? `${k.prefix}…` : "—"}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <ScopeChips scopes={k.scopes} />
                        </td>
                        <td className="px-4 py-3 align-top text-[11px] text-muted-foreground">
                          {k.rate_rpm != null ? `${k.rate_rpm}/min` : "—"}
                        </td>
                        <td className="px-4 py-3 align-top font-mono text-[11px] text-muted-foreground">
                          {k.last_used_at ? when(k.last_used_at) : "never"}
                        </td>
                        <td className="px-4 py-3 align-top font-mono text-[11px] text-muted-foreground">
                          {k.calls_total ?? 0}
                        </td>
                        <td className="px-4 py-3 align-top">
                          {!k.revoked_at && (
                            <div className="flex justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={revokeKey.isPending && revokeKey.variables === k.id}
                                onClick={() => {
                                  if (
                                    confirm(
                                      `Revoke "${k.name}"?\n\nWhatever is calling with it starts getting 401s immediately, and the key cannot be brought back — you would mint a new one and hand that over.`,
                                    )
                                  ) {
                                    revokeKey.mutate(k.id);
                                  }
                                }}
                              >
                                <Trash2 className="size-3.5" /> Revoke
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="people">
          <Section
            title="Share with people"
            description="People you add open this app with their own sign-in and can't open anything else in Chaidojo."
          >
            <ArtifactPeoplePanel artifactRef={id} />
          </Section>
        </TabsContent>

        <TabsContent value="logs">
          <Section
            title={`Log tail (last ${LOG_TAIL})`}
            description="The app's own structured log lines, newest at the bottom."
            actions={
              <>
                {/* The switch carries its own accessible name; the text beside it is
                    a caption, not a label for a native control. */}
                <span className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Switch
                    size="sm"
                    checked={autoRefresh}
                    onCheckedChange={(v) => setAutoRefresh(Boolean(v))}
                    aria-label="Auto-refresh logs"
                  />
                  Auto-refresh
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={logs.isFetching}
                  onClick={() => void logs.refetch()}
                >
                  <RefreshCw className="size-3.5" /> Refresh
                </Button>
              </>
            }
          >
            {logs.isLoading ? (
              <Skeleton className="h-64 w-full rounded-md" />
            ) : logs.isError ? (
              <p className="rounded-lg border border-dashed border-[var(--color-hairline)] bg-card px-4 py-8 text-center text-xs text-muted-foreground">
                Couldn't read the logs —{" "}
                {(logs.error as { message?: string }).message ?? "the app may never have run"}.
              </p>
            ) : logs.data?.lines?.length ? (
              <pre className="max-h-[28rem] overflow-auto rounded-lg border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-4 py-3 font-mono text-[11px] leading-relaxed text-[var(--color-charcoal)]">
                {logs.data.lines.join("\n")}
              </pre>
            ) : (
              <p className="rounded-lg border border-dashed border-[var(--color-hairline)] bg-card px-4 py-8 text-center text-xs text-muted-foreground">
                No log lines — the app hasn't written any since it last started.
              </p>
            )}
          </Section>
        </TabsContent>

        {/* ── Storage ──────────────────────────────────────────────────── */}
        <TabsContent value="storage">
          <StatStrip
            items={[
              {
                label: "Documents",
                value: a.storage_usage?.documents ?? 0,
                hint: "rows in the document store",
              },
              {
                label: "KV keys",
                value: a.storage_usage?.kv ?? 0,
                hint: "key/value entries",
              },
              {
                label: "Blobs",
                value: bytes(a.storage_usage?.blob_bytes ?? 0),
                hint: "uploaded bytes",
              },
              {
                label: "Memory",
                value: bytes(stats.data?.memory_bytes),
                hint: stats.data?.memory_limit_bytes
                  ? `limit ${bytes(stats.data.memory_limit_bytes)}`
                  : "container is cold",
              },
            ]}
          />
          <Section
            title="Runtime"
            description="Live container figures. A cold app reports nothing — that is the resting state, not a fault."
          >
            <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-[var(--color-hairline)] sm:grid-cols-4">
              {[
                ["Warm", stats.data?.warm ? "yes" : "no"],
                ["Status", stats.data?.status ?? a.status],
                [
                  "CPU",
                  stats.data?.cpu_percent != null ? `${stats.data.cpu_percent.toFixed(2)}%` : "—",
                ],
                ["Processes", stats.data?.pids != null ? String(stats.data.pids) : "—"],
              ].map(([label, value]) => (
                <div key={label} className="bg-card px-4 py-3">
                  <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    {label}
                  </dt>
                  <dd className="mt-1 font-mono text-sm tabular-nums">{value}</dd>
                </div>
              ))}
            </dl>
          </Section>
        </TabsContent>

        {/* ── Danger zone ──────────────────────────────────────────────── */}
        <TabsContent value="danger">
          <Section
            title="Danger zone"
            description="Each of these interrupts a URL that someone may be using right now."
          >
            <div className="divide-y divide-[var(--color-hairline-soft)] overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
              <DangerRow
                title={paused ? "Resume" : "Pause"}
                body={
                  paused
                    ? "Serve the app again at its usual address."
                    : "The address stays, but every visitor gets a “paused” notice instead of the app."
                }
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => setPaused.mutate(!paused)}
                  >
                    {paused ? (
                      <>
                        <Play className="size-3.5" /> Resume
                      </>
                    ) : (
                      <>
                        <Pause className="size-3.5" /> Pause
                      </>
                    )}
                  </Button>
                }
              />
              <DangerRow
                title="Restart"
                body="Stop the container now. The next request wakes a fresh one from the same version — in-flight requests are lost."
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => restart.mutate()}
                  >
                    <RotateCw className="size-3.5" /> Restart
                  </Button>
                }
              />
              <DangerRow
                title="Archive"
                body="Take the app offline for good: its address 404s and its container goes away. The bundle and everything it stored are kept."
                action={
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={busy}
                    onClick={() => {
                      if (
                        confirm(
                          `Archive "${a.title}"? Its address stops working. The bundle and stored data are kept.`,
                        )
                      ) {
                        archive.mutate();
                      }
                    }}
                  >
                    <Archive className="size-3.5" /> Archive
                  </Button>
                }
              />
            </div>
          </Section>
        </TabsContent>
      </Tabs>

      {/* Portaled, so they live outside the tab panels and survive a tab
          switch while open. */}
      <DeliveryDrawer
        deliveryId={openDelivery}
        detail={delivery.data}
        loading={delivery.isLoading}
        error={delivery.isError ? delivery.error : null}
        replaying={replay.isPending}
        onReplay={(deliveryId) => replay.mutate(deliveryId)}
        onClose={() => setOpenDelivery(null)}
      />
      <RotatedSecretDialog rotated={rotated} onClose={() => setRotated(null)} />
      <ConnectionDialog
        editing={editing}
        saving={putConnection.isPending}
        onClose={() => setEditing(null)}
        onSave={(body) => putConnection.mutate(body)}
      />
      <NewKeyDialog
        open={mintingKey}
        saving={mintKey.isPending}
        onClose={() => setMintingKey(false)}
        onMint={(body) => mintKey.mutate(body)}
      />
      <MintedKeyDialog minted={minted} onClose={() => setMinted(null)} />
    </div>
  );
}

/** Column header — the same mono/uppercase treatment the Versions table uses,
 *  factored out because Phase D added three more tables. */
function Th({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <th
      className={`px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground ${className ?? ""}`}
    >
      {children}
    </th>
  );
}

function QuietNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-lg border border-dashed border-[var(--color-hairline)] bg-card px-4 py-8 text-center text-xs text-muted-foreground">
      {children}
    </p>
  );
}

/** A 404 from these routes means "this deployment's webgen doesn't have the
 *  surface", which is a state to describe — not an error to apologise for. */
function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/** Overview → Webhooks. The inbound URL is the whole point of the block: it
 *  is what an operator copies to the sender's dashboard. The secret is shown
 *  as webgen's hint only — the value exists exactly once, at rotation. */
function WebhooksSection({
  webhooks,
  rotating,
  onRotate,
}: {
  webhooks: AppWebhook[] | undefined;
  rotating: string | null;
  onRotate: (path: string) => void;
}) {
  if (!webhooks?.length) return null;
  return (
    <Section
      title="Webhooks"
      description="Addresses other systems post to. A delivery is only accepted if it is signed with the current secret — rotate it and the sender stops working until you hand over the new one."
    >
      <div className="divide-y divide-[var(--color-hairline-soft)] overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
        {webhooks.map((w) => (
          <div key={w.path} className="flex flex-col gap-2 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="font-medium text-sm">{w.path}</span>
              {/* The scheme is what the SENDER has to be told, and the replay
                  window is the answer to "why was that delivery rejected" —
                  so both belong on the chip, not in a tooltip. */}
              <StatusBadge
                label={verifyLabel(w)}
                tone={w.verify && w.verify !== "none" ? "ready" : "pending"}
              />
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                disabled={rotating === w.path}
                onClick={() => onRotate(w.path)}
              >
                <KeyRound className="size-3.5" />
                {rotating === w.path ? "Rotating…" : "Rotate secret"}
              </Button>
            </div>
            {/* No URL until the first deploy has minted the path segment —
                showing an empty address bar with a live "Open" button would
                be worse than saying so. */}
            {w.url ? (
              <Addressed label="Inbound" url={w.url} />
            ) : (
              <p className="text-[11px] text-muted-foreground">
                No address yet — deploy the app and it appears here.
              </p>
            )}
            <p className="font-mono text-[11px] text-muted-foreground">
              signing secret {w.signing_secret_hint ? `····${w.signing_secret_hint}` : "not set"}
            </p>
          </div>
        ))}
      </div>
    </Section>
  );
}

/** One delivery, in full. Monospace and copyable because the reason a person
 *  opens this is to paste it to whoever sent it. */
function DeliveryDrawer({
  deliveryId,
  detail,
  loading,
  error,
  replaying,
  onReplay,
  onClose,
}: {
  deliveryId: string | null;
  detail: DeliveryDetail | undefined;
  loading: boolean;
  error: unknown;
  replaying: boolean;
  onReplay: (id: string) => void;
  onClose: () => void;
}) {
  const headers = detail?.headers ?? {};
  const headerText = Object.entries(headers)
    .map(([k, v]) => `${k}: ${v}`)
    .join("\n");
  return (
    <Sheet open={!!deliveryId} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="border-b border-border px-4 py-3">
          <SheetTitle className="text-sm font-semibold">
            {detail?.endpoint ?? "Delivery"}
          </SheetTitle>
          <SheetDescription className="font-mono text-[11px]">
            {deliveryId}
            {detail ? ` · ${when(detail.received_at)} · ${bytes(detail.bytes)}` : ""}
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <Skeleton className="h-40 w-full rounded-md" />
          ) : error ? (
            <QuietNote>
              {isNotFound(error)
                ? "That delivery isn't there any more."
                : ((error as { message?: string }).message ?? "Couldn't read the delivery.")}
            </QuietNote>
          ) : detail ? (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <StatusBadge
                  label={detail.verdict}
                  tone={VERDICT_TONE[detail.verdict] ?? "neutral"}
                />
                <StatusBadge
                  label={detail.status}
                  tone={DELIVERY_STATUS_TONE[detail.status] ?? "neutral"}
                />
                <span className="font-mono text-[11px] text-muted-foreground">
                  {detail.attempts} {detail.attempts === 1 ? "try" : "tries"}
                  {detail.response_status != null ? ` · responded ${detail.response_status}` : ""}
                  {detail.replay_of ? ` · replay of ${detail.replay_of}` : ""}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  disabled={replaying || !deliveryId}
                  onClick={() => {
                    if (
                      deliveryId &&
                      confirm(
                        "Replay this delivery? The handler runs again, with whatever it does to your data.",
                      )
                    ) {
                      onReplay(deliveryId);
                    }
                  }}
                >
                  <Redo2 className="size-3.5" /> Replay
                </Button>
              </div>

              {detail.last_error && (
                <p className="mb-3 rounded-md border border-[var(--color-hairline)] bg-[var(--color-card-tint-rose)] px-3 py-2 text-xs text-[var(--color-semantic-error)]">
                  {detail.last_error}
                </p>
              )}

              <DrawerBlock
                label="Headers"
                text={headerText || "(none recorded)"}
                copyable={!!headerText}
              />
              <DrawerBlock
                label={detail.body_base64 ? "Body (base64)" : "Body"}
                text={detail.body_text || "(empty)"}
                copyable={!!detail.body_text}
                note={
                  [
                    detail.body_base64 ? "Not text — encoded so it can be shown at all." : null,
                    detail.body_clipped ? "Clipped at 64 KB." : null,
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
              />
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DrawerBlock({
  label,
  text,
  copyable,
  note,
}: {
  label: string;
  text: string;
  copyable: boolean;
  note?: string;
}) {
  return (
    <div className="mb-4">
      <div className="mb-1 flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          {label}
        </span>
        {copyable && (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={`Copy ${label.toLowerCase()}`}
            onClick={() => void copy(text, label)}
          >
            <Copy className="size-3 text-muted-foreground" />
          </Button>
        )}
      </div>
      {note && <p className="mb-1 text-[11px] text-muted-foreground">{note}</p>}
      <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-3 py-2 font-mono text-[11px] leading-relaxed text-[var(--color-charcoal)]">
        {text}
      </pre>
    </div>
  );
}

/** The one and only sight of a freshly rotated signing secret.
 *
 *  webgen keeps a hint, not the value, so this dialog is the entire lifetime
 *  of the readable secret: there is no endpoint that returns it again and no
 *  "show me once more". Closing it is irreversible, which is why the copy
 *  button and the warning are both louder than they would otherwise be. */
function RotatedSecretDialog({
  rotated,
  onClose,
}: {
  rotated: { path: string; secret: string; url: string } | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!rotated} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New signing secret for “{rotated?.path}”</DialogTitle>
          <DialogDescription>
            Copy it now. This is the only time it can be read — Chaidojo keeps a hint, not the
            secret. The sender is refused until it signs with this.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 rounded-md border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-3 py-2">
            <code className="min-w-0 flex-1 break-all font-mono text-[12px] text-[var(--color-ink)]">
              {rotated?.secret}
            </code>
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Copy signing secret"
              onClick={() => rotated && void copySecret(rotated.secret)}
            >
              <Copy className="size-3 text-muted-foreground" />
            </Button>
          </div>
          {rotated?.url && <Addressed label="Inbound" url={rotated.url} />}
        </div>
        <DialogFooter>
          <Button onClick={onClose}>I've copied it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BackLink() {
  return (
    <Link
      to="/manage/apps"
      className="mb-4 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="size-3.5" /> All apps
    </Link>
  );
}

function DangerRow({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-6 px-4 py-4">
      <div>
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="mt-1 max-w-prose text-xs leading-relaxed text-muted-foreground">{body}</p>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}

/** The safe half of a connection, in a few characters: the header it uses,
 *  the username it signs in as. Never a value — webgen already filtered it. */
function configSummary(c: Connection): string | null {
  const cfg = c.config ?? {};
  // W5: who signed in at the vendor (Amazon's selling-partner id).
  if (typeof cfg.account === "string" && cfg.account) return `· authorized for ${cfg.account}`;
  const header = typeof cfg.header === "string" ? cfg.header : null;
  const username = typeof cfg.username === "string" ? cfg.username : null;
  if (header) return `· ${header}`;
  if (username) return `· ${username}`;
  return null;
}

/** A connection the agent ASKED for and nobody has filled: the app has a name
 *  for it and no credential behind it. `status` is webgen's word; a missing
 *  `updated_at` is the fallback signal for a webgen that only sends one. */
function isPendingConnection(c: Connection): boolean {
  return c.status === "pending" || c.status === "awaiting";
}

/** Overview → one line about credentials, because the failure mode this page
 *  exists to catch is silent: an app that was built, deployed and is healthy,
 *  sitting there unable to call anything because a card in someone's chat was
 *  never filled in. Renders nothing when the app has no connections at all. */
function ConnectionsSummary({
  connections,
  onOpen,
}: {
  connections: Connection[] | undefined;
  onOpen: () => void;
}) {
  if (!connections?.length) return null;
  const pending = connections.filter(isPendingConnection).length;
  const configured = connections.length - pending;
  return (
    <Section
      title="Connections"
      description="What the app authenticates with. The credentials themselves are write-only — set them here or let the assistant ask for them in the chat."
      actions={
        <Button variant="outline" size="sm" onClick={onOpen}>
          <KeyRound className="size-3.5" /> Credentials
        </Button>
      }
    >
      <div className="flex items-center gap-2 rounded-lg border border-[var(--color-hairline)] bg-card px-4 py-3 text-sm">
        <span className="text-foreground">
          {configured} configured
          {pending > 0 ? `, ${pending} pending` : ""}
        </span>
        {pending > 0 && <StatusBadge label="awaiting the user in chat" tone="pending" />}
      </div>
    </Section>
  );
}

/** Overview → what this app may reach in the workspace.
 *
 *  This is the consent surface for the data plane: an app reads a workspace's
 *  tables only because its manifest said so, and the person who owns that
 *  workspace should be able to see the list without reading YAML. Undeclared
 *  is unreachable, so an empty card is a real statement — "this app reads
 *  nothing" — while NO card means the deployment's webgen does not report
 *  capabilities at all.
 */
function CapabilitiesSection({
  cd,
  approvable,
  approvals,
  pending,
  onApprove,
}: {
  cd?: Capabilities | null;
  /** Which capabilities need a person's approval — served by CD so the
   *  vocabulary lives in one place rather than being retyped here. */
  approvable?: string[];
  approvals?: Approval[];
  /** The capability whose approve/revoke is in flight, if any. */
  pending?: string | null;
  onApprove?: (capability: string, approve: boolean) => void;
}) {
  if (!cd) return null;
  const keys = Object.keys(cd).sort();
  const needsApproval = new Set(approvable ?? []);
  const approved = new Map((approvals ?? []).map((a) => [a.capability, a]));
  return (
    <Section
      title="Capabilities"
      description="What this app declared it may do in the workspace. Anything not listed here is unreachable to it — the platform refuses the call, naming the key. The ACTION capabilities also need your approval: a declaration alone does nothing."
    >
      {keys.length === 0 ? (
        <div className="rounded-lg border border-[var(--color-hairline)] bg-card px-4 py-3 text-sm text-muted-foreground">
          Declared nothing — this app cannot read the workspace.
        </div>
      ) : (
        <ul className="divide-y divide-[var(--color-hairline-soft)] overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
          {keys.map((key) => {
            const gated = needsApproval.has(key);
            const row = approved.get(key);
            return (
              <li key={key} className="px-4 py-2.5">
                <div className="flex items-baseline justify-between gap-4">
                  <span className="font-mono text-xs text-foreground">{key}</span>
                  <span className="text-right text-xs text-muted-foreground">
                    {capabilityValue(cd[key])}
                  </span>
                </div>
                {gated && (
                  <div className="mt-1.5 flex items-center justify-between gap-3">
                    <span className="text-[11px] text-muted-foreground">
                      {row ? (
                        <>
                          Approved by{" "}
                          <span className="text-foreground">
                            {row.approved_by_email ?? "an admin"}
                          </span>
                          {row.created_at ? ` ${when(row.created_at)}` : ""}
                        </>
                      ) : (
                        "Declared, but not approved — every call is refused until you approve it."
                      )}
                    </span>
                    <Button
                      size="sm"
                      variant={row ? "outline" : "default"}
                      disabled={pending === key || !onApprove}
                      onClick={() => onApprove?.(key, !row)}
                    >
                      {pending === key ? "Saving…" : row ? "Revoke" : "Approve"}
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

/** Workflow runs this app started (F4).
 *
 *  The other half of an approval: having granted `runs.start`, an operator
 *  should be able to see what the app spent it on without leaving the page.
 *  Renders nothing when the app has never started one — an empty table on
 *  every app that has no such capability would be noise. */
function AppWorkflowRunsSection({ runs }: { runs?: AppWorkflowRun[] }) {
  if (!runs || runs.length === 0) return null;
  return (
    <Section
      title="Runs started by this app"
      description="Workflow runs started through cd.runs.start. Each one spends model budget against the app's daily allowance."
    >
      <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
        <table className="w-full text-sm">
          <thead className="bg-[var(--color-surface-soft)]">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                Workflow
              </th>
              <th className="w-28 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                Phase
              </th>
              <th className="w-32 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                Started
              </th>
              <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                Run
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-hairline-soft)]">
            {runs.map((r) => (
              <tr key={r.run_id}>
                <td className="px-4 py-3 font-mono text-xs">{r.workflow_id}</td>
                <td className="px-4 py-3 text-xs">{r.phase}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  {when(r.started_at ?? r.created_at)}
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-muted-foreground">
                  {r.run_id}
                  {r.error_message && (
                    <div className="mt-0.5 font-sans text-[11px] text-[var(--color-semantic-danger)]">
                      {r.error_message}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

/** One capability's value as a phrase. `"*"` is spelled out because "the whole
 *  workspace" and a wildcard glyph read very differently to someone deciding
 *  whether to keep an app installed. */
function capabilityValue(value: unknown): string {
  if (value === true) return "yes";
  if (value === false || value == null) return "no";
  if (value === "*") return "the whole workspace";
  if (Array.isArray(value)) {
    if (value.length === 0) return "nothing";
    return value.map((v) => (v === "*" ? "the whole workspace" : String(v))).join(", ");
  }
  return String(value);
}

/** Add or replace one connection.
 *
 *  Write-only by construction: opening it on an existing connection preloads
 *  the name, kind and URL — the things that are safe to show — and leaves the
 *  credential boxes empty, because there is nothing to preload them WITH. An
 *  update retypes the secret or omits it (webgen then keeps the stored one and
 *  changes only the rest).
 */
function ConnectionDialog({
  editing,
  saving,
  onClose,
  onSave,
}: {
  editing: Connection | "new" | null;
  saving: boolean;
  onClose: () => void;
  onSave: (body: {
    name: string;
    kind: string;
    base_url: string;
    config: Record<string, unknown>;
    secret?: Record<string, unknown>;
  }) => void;
}) {
  const existing = editing && editing !== "new" ? editing : null;
  // Remounting on identity change is what resets the boxes between a "new"
  // and an "edit" — cheaper and harder to get wrong than an effect that
  // syncs six fields.
  const key = existing ? `edit-${existing.name}` : "new";
  return (
    <Dialog open={!!editing} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        {editing && (
          <ConnectionForm
            key={key}
            existing={existing}
            saving={saving}
            onCancel={onClose}
            onSave={onSave}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/** `Name: value` lines → a header map. A line with no colon is skipped. */
/** `name=value` a line → the consent link's extra parameters. */
function paramLines(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const at = line.indexOf("=");
    if (at <= 0) continue;
    out[line.slice(0, at).trim()] = line.slice(at + 1).trim();
  }
  return out;
}

function headerLines(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split(/\r?\n/)) {
    const at = line.indexOf(":");
    if (at <= 0) continue;
    const name = line.slice(0, at).trim();
    if (name) out[name] = line.slice(at + 1).trim();
  }
  return out;
}

function ConnectionForm({
  existing,
  saving,
  onCancel,
  onSave,
}: {
  existing: Connection | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (body: {
    name: string;
    kind: string;
    base_url: string;
    config: Record<string, unknown>;
    secret?: Record<string, unknown>;
  }) => void;
}) {
  const [name, setName] = useState(existing?.name ?? "");
  const [kind, setKind] = useState(existing?.kind ?? "bearer");
  const [baseUrl, setBaseUrl] = useState(existing?.base_url ?? "");
  /** Every box in one record, keyed by the CONNECTION_KINDS field name. The
   *  non-secret ones preload from `config` (webgen's filtered display form);
   *  the secret ones start empty and stay empty, because there is nothing to
   *  preload them WITH. */
  const [values, setValues] = useState<Record<string, string>>(() => {
    const cfg = (existing?.config ?? {}) as Record<string, unknown>;
    const seed: Record<string, string> = {};
    for (const [k, v] of Object.entries(cfg)) {
      if (typeof v === "string") seed[k === "auth" ? "auth_style" : k] = v;
    }
    // `header` and `query` name the API key's place for api_key, the token's
    // for the OAuth2 kinds: the box the kind draws picks the one it means.
    if (typeof cfg.header === "string") seed.token_header = cfg.header;
    if (typeof cfg.query === "string") seed.token_query = cfg.query;
    if (cfg.headers && typeof cfg.headers === "object") {
      seed.extra_headers = Object.entries(cfg.headers as Record<string, unknown>)
        .map(([h, v]) => `${h}: ${String(v)}`)
        .join("\n");
    }
    if (cfg.authorize_params && typeof cfg.authorize_params === "object") {
      seed.authorize_params = Object.entries(cfg.authorize_params as Record<string, unknown>)
        .map(([k, v]) => `${k}=${String(v)}`)
        .join("\n");
    }
    // The call list is shown back and sent back: a save that dropped it would
    // quietly make a read-only connection able to write.
    if (Array.isArray(cfg.allow)) {
      seed.allow_calls = (cfg.allow as { method?: string; path_prefix?: string }[])
        .map((e) => `${e.method ?? ""} ${e.path_prefix ?? ""}`.trim())
        .join("\n");
    }
    return seed;
  });

  // The literal always has a first entry; `noUncheckedIndexedAccess` just
  // cannot see that through the array type.
  const shape = CONNECTION_KINDS.find((k) => k.value === kind) ?? CONNECTION_KINDS[0]!;
  const keyKind = KEY_KINDS.has(kind);
  const fields = [
    ...(shape.fields as readonly string[]),
    ...(kind === "header_set" || keyKind ? [] : EXTRA_HEADER_FIELDS),
    ...(keyKind ? [] : ["allow_calls"]),
  ];
  const set = (field: string, v: string) => setValues((prev) => ({ ...prev, [field]: v }));
  const val = (field: string) => values[field] ?? "";
  /** Which secret boxes the person has asked to see while typing. Never on by default. */
  const [shown, setShown] = useState<Record<string, boolean>>({});

  const nameOk = /^[a-z0-9][a-z0-9_-]{0,63}$/.test(name);
  const secretFields = fields.filter((f) => CONNECTION_FIELDS[f]?.secret);
  // Which non-secret boxes the kind cannot do without. `scope`/`audience` are
  // optional everywhere they appear, and the form says so in their hint.
  const optional = new Set([
    "scope", "audience", "auth_style", "token_header", "token_query", "token_method",
    "extra_headers", "secret_headers", "credentials_in", "allow_calls",
    "authorize_url", "authorize_params",
  ]);
  const requiredConfig = fields.filter((f) => !CONNECTION_FIELDS[f]?.secret && !optional.has(f));
  const hasCredential = secretFields.some((f) => f !== "secret_headers" && val(f).trim().length > 0);
  // An update may leave the credential boxes empty (keep what is stored); a
  // NEW connection that needs one cannot.
  const ready =
    nameOk &&
    (keyKind || baseUrl.trim().length > 0) &&
    requiredConfig.every((f) => val(f).trim().length > 0) &&
    (secretFields.length === 0 || !!existing || hasCredential);

  /** The kind's shape on the wire. Config is the half that is safe to show
   *  back on the list; `secret` is what webgen stores write-only, and omitting
   *  it entirely keeps the credential already there. */
  const buildPayload = (): {
    config: Record<string, unknown>;
    secret?: Record<string, unknown>;
  } => {
    const config: Record<string, unknown> = {};
    // The box → webgen's config key. Anything the page never drew is left
    // out, and webgen keeps what is stored (2.17.0).
    const KEY: Record<string, string> = {
      auth_style: "auth",
      token_header: "header",
      token_query: "query",
    };
    for (const f of fields) {
      if (CONNECTION_FIELDS[f]?.secret) continue;
      const v = val(f).trim();
      if (f === "extra_headers") {
        if (v || existing) config.headers = v ? headerLines(v) : null;
        continue;
      }
      if (f === "allow_calls") {
        if (v || existing) config.allow = v ? allowLines(v) : null;
        continue;
      }
      if (f === "authorize_params") {
        if (v || existing) config.authorize_params = v ? paramLines(v) : null;
        continue;
      }
      if (!v) continue;
      config[KEY[f] ?? f] = f === "token_method" ? v.toUpperCase() : v;
    }
    const withHeaders = (payload: Record<string, unknown> | undefined) => {
      const typed = val("secret_headers").trim();
      if (!typed) return payload;
      return { ...(payload ?? {}), headers: headerLines(typed) };
    };
    const shaped = shapeSecret(config);
    const secretOut = withHeaders(shaped.secret);
    return secretOut ? { config: shaped.config, secret: secretOut } : { config: shaped.config };
  };

  const shapeSecret = (
    config: Record<string, unknown>,
  ): { config: Record<string, unknown>; secret?: Record<string, unknown> } => {
    const secret = val("secret");
    switch (kind) {
      case "basic":
        return val("password")
          ? { config, secret: { username: val("username").trim(), password: val("password") } }
          : { config };
      case "header_set": {
        // webgen keeps a header set's shape in its secret, so its config is empty.
        const { header: _name, ...rest } = config;
        return secret
          ? { config: rest, secret: { headers: { [val("header").trim()]: secret } } }
          : { config: rest };
      }
      case "oauth2_client_credentials":
        return secret ? { config, secret: { client_secret: secret } } : { config };
      case "oauth2_refresh": {
        const payload: Record<string, unknown> = {};
        if (val("refresh_token")) payload.refresh_token = val("refresh_token");
        if (secret) payload.client_secret = secret;
        return Object.keys(payload).length ? { config, secret: payload } : { config };
      }
      case "aws_sigv4":
        return secret ? { config, secret: { secret_access_key: secret } } : { config };
      case "oauth2_password": {
        // The username is the secret's, not the config's: webgen keeps them together.
        const { username: _user, ...rest } = config;
        return val("password")
          ? { config: rest, secret: { username: val("username").trim(), password: val("password") } }
          : { config: rest };
      }
      case "hmac_key":
        return val("key").trim() ? { config: {}, secret: { key: val("key").trim() } } : { config: {} };
      default:
        return secret ? { config, secret: { value: secret } } : { config };
    }
  };

  const submit = () => {
    const { config, secret } = buildPayload();
    onSave({ name: name.trim(), kind, base_url: keyKind ? "" : baseUrl.trim(), config, secret });
    // The typed credential lives in this component's state and nowhere else.
    // Clear it the moment it is handed over, so a dialog left open behind a
    // failed save is not still holding it.
    setValues((prev) => {
      const next = { ...prev };
      for (const f of secretFields) delete next[f];
      return next;
    });
  };

  /** What the box is called for THIS kind. One label table plus a couple of
   *  per-kind overrides beats a label per kind per field. */
  const labelFor = (field: string): string => {
    if (field === "secret") {
      if (kind === "bearer") return "Token";
      if (kind === "oauth2_client_credentials" || kind === "oauth2_refresh") return "Client secret";
      if (kind === "aws_sigv4") return "Secret access key";
    }
    return CONNECTION_FIELDS[field]?.label ?? field;
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>{existing ? `Set "${existing.name}"` : "Add a connection"}</DialogTitle>
        <DialogDescription>
          The app calls this by name — it never holds the credential itself. Nothing typed here can
          be read back, from this page or anywhere else.
        </DialogDescription>
      </DialogHeader>

      <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto py-1">
        <Field label="Name" hint="Lowercase, what the app's code calls it.">
          <Input
            value={name}
            disabled={!!existing}
            onChange={(e) => setName(e.target.value)}
            placeholder="stripe"
            className="font-mono text-sm"
          />
        </Field>
        <Field label="Kind" hint={shape.hint}>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="h-9 w-full rounded-md border border-[var(--color-hairline)] bg-card px-2.5 text-sm"
          >
            {CONNECTION_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </Field>
        {!keyKind && (
          <Field
            label="Base URL"
            hint="Everything the app may reach through this connection lives under it."
          >
            <Input
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://api.stripe.com"
              className="font-mono text-sm"
            />
          </Field>
        )}
        {fields.map((field) => {
          const spec = CONNECTION_FIELDS[field];
          if (!spec) return null;
          const hint =
            spec.secret && existing ? "Leave empty to keep the stored credential." : spec.hint;
          return (
            <Field key={field} label={labelFor(field)} hint={hint}>
              {spec.multiline ? (
                <Textarea
                  value={val(field)}
                  onChange={(e) => set(field, e.target.value)}
                  placeholder={spec.placeholder}
                  autoComplete="off"
                  spellCheck={false}
                  rows={2}
                  className="font-mono text-sm"
                />
              ) : spec.secret ? (
                <div className="relative">
                  <Input
                    type={shown[field] ? "text" : "password"}
                    value={val(field)}
                    onChange={(e) => set(field, e.target.value)}
                    placeholder={spec.placeholder}
                    autoComplete="off"
                    spellCheck={false}
                    className="pr-10 font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShown((prev) => ({ ...prev, [field]: !prev[field] }))}
                    aria-label={shown[field] ? `Hide ${labelFor(field).toLowerCase()}` : `Show ${labelFor(field).toLowerCase()}`}
                    aria-pressed={!!shown[field]}
                    className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-3 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  >
                    {shown[field] ? (
                      <EyeOff className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    )}
                  </button>
                </div>
              ) : (
                <Input
                  type="text"
                  value={val(field)}
                  onChange={(e) => set(field, e.target.value)}
                  placeholder={spec.placeholder}
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono text-sm"
                />
              )}
            </Field>
          );
        })}
        {!nameOk && name.length > 0 && (
          <p className="text-[11px] text-[var(--color-semantic-error)]">
            Lowercase letters, digits, dashes and underscores only.
          </p>
        )}
      </div>

      <DialogFooter>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button size="sm" onClick={submit} disabled={!ready || saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogFooter>
    </>
  );
}

/** A webhook's chip: the scheme the sender must sign with, and how old a
 *  delivery may be before it is refused as a replay. "unsigned" is amber, not
 *  grey: it means the endpoint accepts anything anybody posts. */
function verifyLabel(w: AppWebhook): string {
  const scheme = w.verify && w.verify !== "none" ? w.verify : "unsigned";
  const tolerance = (w as { tolerance_s?: number | null }).tolerance_s;
  return tolerance ? `${scheme} · replay window ${tolerance} s` : scheme;
}

/** The line under a connection's status chip: why it is failing, when its
 *  token runs out, how often it has failed today, and what the last Test
 *  said. Everything here is a FACT about calls that happened — a page that
 *  only ever said "configured" is the one that lets a silently-expired
 *  credential sit there until an app fails at 3am. */
function ConnectionHealth({
  connection,
  test,
}: {
  connection: Connection;
  test?: ConnectionTest;
}) {
  const expiry = connection.token_expires_at;
  const expired = expiry ? new Date(expiry).getTime() < Date.now() : false;
  const failures = connection.failures_24h ?? 0;
  return (
    <div className="mt-1 flex flex-col gap-0.5 text-[11px] text-muted-foreground">
      {isPendingConnection(connection) && <span>awaiting the user in chat</span>}
      {connection.last_error && (
        <span className="text-[var(--color-semantic-error)]">{connection.last_error}</span>
      )}
      {failures > 0 && <span>{failures} failed in 24h</span>}
      {/* The short-lived access token Chaidojo fetched with the stored credential. One that
          has run out is routine — the next call fetches another — so it is never shown as a
          fault; a credential that stops working shows as `last_error` and failures above. */}
      {expiry && (
        <span title="The short-lived access token Chaidojo fetches with the stored credential — renewed automatically.">
          {expired ? "access token renews on next call" : `access token expires ${when(expiry)}`}
        </span>
      )}
      {test && (
        <span
          className={
            test.ok ? "text-[var(--color-semantic-success)]" : "text-[var(--color-semantic-error)]"
          }
        >
          tested:{" "}
          {test.ok ? `${test.status ?? 200} in ${millis(test.took_ms)}` : (test.error ?? "failed")}
        </span>
      )}
    </div>
  );
}

/** A connection's rotation record: which version, four characters of hint,
 *  when, and who. No version is readable — not the current one and not an old
 *  one — so this answers "did the card in the chat actually replace what I set
 *  on Tuesday" without answering "what is it". */
function ConnectionHistory({
  versions,
  loading,
  error,
}: {
  versions: ConnectionVersion[] | undefined;
  loading: boolean;
  error: unknown;
}) {
  if (loading) return <Skeleton className="h-12 w-full rounded-md" />;
  if (isNotFound(error)) {
    return (
      <p className="text-[11px] text-muted-foreground">
        This deployment's webgen doesn't keep a rotation history yet.
      </p>
    );
  }
  if (error) {
    return <p className="text-[11px] text-muted-foreground">Couldn't read the history.</p>;
  }
  if (!versions?.length) {
    return (
      <p className="text-[11px] text-muted-foreground">
        Set once, never rotated — there is only the credential in use.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1">
      {versions.map((v) => (
        <li key={v.version} className="flex items-baseline gap-3 text-[11px]">
          <span className="w-8 shrink-0 font-mono text-muted-foreground">v{v.version}</span>
          {/* The hint is webgen's fingerprint (`sha256:1a2b3c4d`), not a
              masked tail — printing dots in front of it would read as four
              real characters of the key, which is exactly what it is not. */}
          <span className="font-mono text-muted-foreground">
            {v.hint ? (v.hint.includes(":") ? v.hint : `····${v.hint}`) : "no hint"}
          </span>
          <span className="text-muted-foreground">{when(v.rotated_at)}</span>
          <span className="truncate text-foreground">{v.actor || "unknown"}</span>
        </li>
      ))}
    </ul>
  );
}

/** A key's grants, as chips. No scopes at all is not "none" — webgen reads an
 *  empty list as the whole API — so it says so in words. */
function ScopeChips({ scopes }: { scopes: KeyScope[] | undefined }) {
  if (!scopes?.length) {
    return <span className="text-[11px] text-muted-foreground">everything the app serves</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {scopes.map((sc) => (
        <span
          key={`${sc.path ?? ""}-${(sc.methods ?? []).join()}`}
          className="rounded-sm bg-[var(--color-surface-soft)] px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground"
        >
          {(sc.methods?.length ? sc.methods.join(", ") : "any method") +
            (sc.path ? ` · ${sc.path}` : "")}
        </span>
      ))}
    </div>
  );
}

/** Mint a key: a name, the paths it may call, and an optional ceiling.
 *
 *  Scopes are rows rather than a free-text box because the thing being written
 *  down is a permission: "GET api/summary" is a decision somebody can check,
 *  and a mistyped glob is one they cannot. */
function NewKeyDialog({
  open,
  saving,
  onClose,
  onMint,
}: {
  open: boolean;
  saving: boolean;
  onClose: () => void;
  onMint: (body: { name: string; scopes: KeyScope[]; rate_rpm?: number | null }) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        {open && <NewKeyForm key="mint" saving={saving} onCancel={onClose} onMint={onMint} />}
      </DialogContent>
    </Dialog>
  );
}

const KEY_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;

function NewKeyForm({
  saving,
  onCancel,
  onMint,
}: {
  saving: boolean;
  onCancel: () => void;
  onMint: (body: { name: string; scopes: KeyScope[]; rate_rpm?: number | null }) => void;
}) {
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  // Each row carries its own id: an index key would re-use the removed row's
  // state on the one that slides up into its place.
  const [rows, setRows] = useState<{ id: string; methods: string[]; path: string }[]>([
    { id: "s0", methods: ["GET"], path: "" },
  ]);
  const nameOk = /^[a-z0-9][a-z0-9_-]{0,63}$/.test(name);
  const scopes = rows
    .map((r) => ({ methods: r.methods, path: r.path.trim() }))
    .filter((r) => r.path.length > 0);
  const ready = nameOk && scopes.length > 0 && scopes.every((r) => r.methods.length > 0);

  return (
    <>
      <DialogHeader>
        <DialogTitle>New API key</DialogTitle>
        <DialogDescription>
          The value is shown once, on the next screen. Chaidojo keeps the prefix and a hash — if it
          is lost, the fix is to revoke it and mint another.
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-3 py-1">
        <Field label="Name" hint="Lowercase. Who or what is calling — “partner-read”.">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="partner-read"
            className="font-mono text-sm"
          />
        </Field>

        <div className="flex flex-col gap-1.5">
          <Label className="text-xs font-medium">Can call</Label>
          {rows.map((row, i) => (
            <div key={row.id} className="flex items-center gap-1.5">
              <div className="flex flex-wrap gap-1">
                {KEY_METHODS.map((m) => {
                  const on = row.methods.includes(m);
                  return (
                    <button
                      type="button"
                      key={m}
                      onClick={() =>
                        setRows((prev) =>
                          prev.map((r, j) =>
                            j === i
                              ? {
                                  ...r,
                                  methods: on
                                    ? r.methods.filter((x) => x !== m)
                                    : [...r.methods, m],
                                }
                              : r,
                          ),
                        )
                      }
                      className={`rounded-sm px-1.5 py-0.5 font-mono text-[10px] ${
                        on
                          ? "bg-[var(--color-brand-terracotta)]/15 text-[var(--color-brand-terracotta-deep)]"
                          : "bg-[var(--color-surface-soft)] text-muted-foreground"
                      }`}
                    >
                      {m}
                    </button>
                  );
                })}
              </div>
              <Input
                value={row.path}
                onChange={(e) =>
                  setRows((prev) =>
                    prev.map((r, j) => (j === i ? { ...r, path: e.target.value } : r)),
                  )
                }
                placeholder="api/summary"
                className="h-8 flex-1 font-mono text-xs"
              />
              {rows.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Remove this scope"
                  onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}
                >
                  <Trash2 className="size-3 text-muted-foreground" />
                </Button>
              )}
            </div>
          ))}
          <button
            type="button"
            className="self-start text-[11px] text-muted-foreground hover:text-foreground"
            onClick={() =>
              setRows((prev) => [
                ...prev,
                { id: `s${Date.now()}${prev.length}`, methods: ["GET"], path: "" },
              ])
            }
          >
            + another path
          </button>
          <p className="text-[11px] text-muted-foreground">
            A path under the app's own API. An unscoped key would reach everything it serves, so at
            least one is required here.
          </p>
        </div>

        <Field label="Rate limit" hint="Requests a minute, 1–600. Leave empty for the default, 60.">
          <Input
            value={rate}
            inputMode="numeric"
            onChange={(e) => setRate(e.target.value.replace(/[^0-9]/g, ""))}
            placeholder="60"
            className="font-mono text-sm"
          />
        </Field>

        {!nameOk && name.length > 0 && (
          <p className="text-[11px] text-[var(--color-semantic-error)]">
            Lowercase letters, digits, dashes and underscores only.
          </p>
        )}
      </div>

      <DialogFooter>
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!ready || saving}
          onClick={() =>
            onMint({
              name: name.trim(),
              scopes,
              rate_rpm: rate ? Number(rate) : null,
            })
          }
        >
          {saving ? "Minting…" : "Mint key"}
        </Button>
      </DialogFooter>
    </>
  );
}

/** The minted value, once. Same posture as `RotatedSecretDialog`: it arrives,
 *  it is shown in the same breath, and it is never written anywhere a refetch
 *  could try to get it back. */
function MintedKeyDialog({
  minted,
  onClose,
}: {
  minted: { name: string; key: string } | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!minted} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>API key “{minted?.name}”</DialogTitle>
          <DialogDescription>
            Copy it now. This is the only time it can be read — Chaidojo keeps the prefix and a
            hash, not the key. If it is lost, revoke it and mint another.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 rounded-md border border-[var(--color-hairline)] bg-[var(--color-surface-soft)] px-3 py-2">
          <code
            data-minted-key
            className="min-w-0 flex-1 break-all font-mono text-[12px] text-[var(--color-ink)]"
          >
            {minted?.key}
          </code>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Copy API key"
            onClick={() => minted && void copySecret(minted.key, "API key")}
          >
            <Copy className="size-3 text-muted-foreground" />
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>I've copied it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs font-medium">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
